/**
 * 航跡・地点・競技エリアのレイヤー。
 *
 * 開発メモの「複数チームが重なって見づらくなる問題」を見据え、
 * 航跡は**高度で色分け**する。単なる線ではなく、どの高度をどこで飛んだかが
 * 一目で分かることが、風を読むうえでの出発点になる。
 */

import type { ExpressionSpecification, FilterSpecification, GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl';
import type { CircleArea, FixSample, MarkPoint } from '@gv4d/core';
import { effectiveCeilingFt, toGridCode, toUtm } from '@gv4d/core';
import { feetToMeters, metersToFeet } from '../units.ts';
import type {
  CompMapManifest,
  NotamReferenceCircle,
  NotamRegime,
  PzSaCircleZone,
  PzSaPolygonZone,
  PzSaTubeZone,
  PzSaZone,
} from '../data/venue.ts';
import { RidgeLineLayer } from './ridge-line-layer.ts';
import { TubeLayer } from './tube-layer.ts';
import { buildTubeMesh, type TubeMeshInput } from './tube-mesh.ts';

/**
 * 3D航跡の壁の天面に沿わせる稜線ライン（カスタムWebGLレイヤー）。
 * スタイル差し替え（setStyle）のたびに`addOverlayLayers`が呼ばれ直すので、
 * インスタンスはモジュール直下で1つずつ使い回す（onAdd/onRemoveは
 * MapLibre側がスタイル差し替えのたびに呼び直してくれる）。
 * main.tsのupdateRidgeLines()がredraw()のたびに.setStrips()で頂点を渡す。
 */
export const trackRidgeLine = new RidgeLineLayer('gv-track-3d-ridge');
export const liveTrackRidgeLine = new RidgeLineLayer('gv-live-track-3d-ridge');
/** tube（線状PZ）の3D表示。main.tsのupdatePzSaTubes()がメッシュを渡す */
export const pzsaTubeLayer = new TubeLayer('gv-pzsa-tube-3d');

export const TRACK_SOURCE = 'gv-track';
/** 3D航跡（fill-extrusionの壁）。2D版（TRACK_SOURCE）と同じフライトから作る */
export const TRACK_3D_SOURCE = 'gv-track-3d';
export const POINT_SOURCE = 'gv-track-points';
export const GOAL_SOURCE = 'gv-goals';
/** 宣言したゴール（一覧に入れたもの）。会場のゴール地点とは別に持つ */
export const DECLARED_SOURCE = 'gv-declared';
/** 高度(altFt)付きの宣言ゴール（3Dの高度ピン用）。DECLARED_SOURCEのうち高度があるものだけ */
export const GOAL_3D_SOURCE = 'gv-goal-3d';
/** 同、柱の先端に置く球 */
export const GOAL_BALL_3D_SOURCE = 'gv-goal-3d-ball';
export const AREA_SOURCE = 'gv-areas';
export const LIVE_TRACK_SOURCE = 'gv-live-track';
/** ライブ端末の3D航跡。2D版（LIVE_TRACK_SOURCE）と同じ端末データから作る */
export const LIVE_TRACK_3D_SOURCE = 'gv-live-track-3d';
export const LIVE_POINT_SOURCE = 'gv-live-points';
/** ライブ端末の現在位置（3Dの矢印型の柱用）。2D版（LIVE_POINT_SOURCE）と同じ端末データから作る */
export const LIVE_POINT_3D_SOURCE = 'gv-live-point-3d';
/** 同、柱の先端に置く球 */
export const LIVE_POINT_BALL_3D_SOURCE = 'gv-live-point-3d-ball';
/** 30分後の到着予測（原典GoalViewの「ノルデン照準器」相当） */
export const LIVE_PREDICT_SOURCE = 'gv-live-predict';
/** 他機が宣言したゴール（$_TVMARK 由来）。原典GVでは○で表示される */
export const LIVE_MARK_SOURCE = 'gv-live-marks';
/** UTM方眼線（1kmグリッド）。会場ごとに utm-grid.ts が組み立てる */
export const UTM_GRID_SOURCE = 'gv-utm-grid';
/**
 * 鉛直断面ビューで切っている断面線と、その奥行き（板の厚み）の帯。
 * 断面ビューを開いている間だけ表示し、閉じたら消す。
 */
export const SECTION_SOURCE = 'gv-section';
/**
 * LRN/3DT戦略プランナー（gv-core の lrn.ts / three-dt-strategy.ts）が出した
 * 推奨ルートの参考描画。ダイアログを開いて「計算」した結果だけを描き、
 * 閉じたら消す（SECTION_SOURCE と同じ「一時的な参考描画」の扱い）。
 */
export const LRN3DT_PLAN_SOURCE = 'gv-lrn3dt-plan';
/**
 * プランナーの3D表示。2D版（LRN3DT_PLAN_SOURCE）と同じ計算結果から作るが、
 * fill-extrusion は「高さの式」がフィーチャの属性で決まるため、
 * 壁（`kind:'wall'`、`alt`[m]の高さまで押し上げる）と
 * 立体（`kind:'volume'`、`floorM`〜`ceilingM` を埋める）で式が違う。
 * 1つのソースに入れ、レイヤー側で `kind` で振り分ける。
 */
export const LRN3DT_PLAN_3D_SOURCE = 'gv-lrn3dt-plan-3d';
/**
 * 選択中ゴールへの点線（現在地→ゴール、方位・距離のラベル付き）。
 * モードがOFF、ゴール未選択、現在地不明のいずれかの間はデータを空にする
 * （LRN3DT_PLAN_SOURCEと同じ「必要なときだけ描く」扱い）。
 */
export const GOAL_BEARING_SOURCE = 'gv-goal-bearing';
/**
 * 3DT（3Dシェイプタスク）の設定空域。2D（塗り・線・ラベル）と
 * 3D（下限〜上限を埋める箱）が同じポリゴンを使うので、NOTAM_SOURCE と
 * 同じくソースを1つ共有する。
 */
export const AIRSPACE_SOURCE = 'gv-airspace';
/** PZ（禁止・要注意区域）/ SA（要注意エリア）のうち円で表せるもの */
export const PZSA_SOURCE = 'gv-pzsa';
/** 同、矩形など「地図参照」の多角形 */
export const PZSA_POLY_SOURCE = 'gv-pzsa-poly';
/**
 * インポートしたスケッチ（TV/TaskviewのXDD等、OziExplorerの.plt形式）。
 * 会場の一部ではなく当日ファイルで受け取る一時的なオーバーレイなので、
 * PZ/SAとは別ソースにする（data/sketch.ts参照）。
 */
export const SKETCH_SOURCE = 'gv-sketch';
/**
 * NOTAM高度制限区域（通称BluePZ）。2D（塗り・線・ラベル）と3D（上空の薄板）の
 * 両方が同じポリゴン形状を使うので、ソースを1つだけ共有する
 * （ゴールピンのような2D=点/3D=小ポリゴンという使い分けが要らない）。
 */
export const NOTAM_SOURCE = 'gv-notam';
/**
 * 飛行場中心の参考円（例: 宇都宮飛行場5NM/10NM/15NM）。BluePZ本体
 * （NOTAM_SOURCE）とは別ソースにする。侵入警告の対象にせず、破線の
 * 輪郭線のみを表示する参考情報として明確に区別するため
 */
export const NOTAM_REF_SOURCE = 'gv-notam-ref';
/**
 * 送電線（高圧線）・鉄道・幹線道路・高速道路。scripts/build-hazard-lines.mjs で
 * OpenStreetMapから切り出す（会場ごとの hazards.geojson、無ければ空）
 */
export const HAZARD_SOURCE = 'gv-hazards';
/**
 * 競技地図（原典GoalViewのPZ/ゴールが描き込まれた公式地図画像）を
 * `scripts/build-compmap-tiles.mjs` で焼いたrasterタイル。既定オフの
 * オプションオーバーレイ（docs/04 §4）。
 */
export const COMPMAP_SOURCE = 'gv-compmap';
export const COMPMAP_LAYER = 'gv-compmap-raster';
/** 不透明度の既定値。薄すぎると重ねた意味が無く、濃すぎると下の地図・航跡が読めなくなる中間 */
export const DEFAULT_COMPMAP_OPACITY = 0.7;

/**
 * 3D表示用の地形（標高DEM）ソース。Mapterhorn配布のPMTiles
 * （terrarium エンコーディング、webp、512px）を使う。
 * このソースURLはオンライン（惑星全体のPMTilesをRangeで都度取得）・
 * オフライン（会場ぶんの事前ダウンロード済みファイル）のどちらもありうるが、
 * どちらを渡すかの判断は呼び出し側（main.tsのapplyTerrainState）が行う。
 */
export const TERRAIN_SOURCE = 'gv-terrain';

export interface CompMapLayerConfig {
  /** `{z}/{x}/{y}` プレースホルダを含むタイルURL（相対パスでよい） */
  tileUrlTemplate: string;
  manifest: CompMapManifest;
}
/** kind ⇒ 色。送電線=黄、鉄道=朱色、幹線道路=赤、高速道路=緑（本人指定） */
const HAZARD_COLORS: Record<string, string> = {
  power: '#ffd400',
  rail: '#e2451c',
  arterial: '#ff3b30',
  expressway: '#34c759',
};
const HAZARD_WIDTHS: Record<string, number> = {
  power: 1.5,
  rail: 2,
  arterial: 2.5,
  expressway: 3,
};
/**
 * 幹線道路（kind:'arterial'）のうち、どこまで細かいレベルを表示するか。
 * scripts/build-hazard-lines.mjs は trunk/primary/secondary/tertiary（会場に
 * よっては後から4を追加）をまとめて取得してあるので、レベルの調整は
 * Overpassへの再問い合わせ無しに、このフィルターの切り替えだけで完結する
 * （feature.properties.roadLevelを使う）。
 */
export const DEFAULT_HAZARD_ROAD_LEVEL = 2;
/**
 * gv-hazard-line（鉄道以外）に適用するフィルター。鉄道は見た目が幹線道路と
 * 紛らわしい（どちらも実線）ため gv-hazard-rail-line という別レイヤーに分けて
 * 破線で描く。ここでは鉄道を除外しつつ、幹線道路のレベル絞り込みも行う
 */
export function hazardRoadLevelFilter(level: number): ExpressionSpecification {
  return [
    'all',
    ['!=', ['get', 'kind'], 'rail'],
    ['any', ['!=', ['get', 'kind'], 'arterial'], ['<=', ['get', 'roadLevel'], level]],
  ] as ExpressionSpecification;
}
/**
 * 自機位置（ローカルのGPS）。サーバーへの送信とは別に、取得した瞬間から
 * 即座に自端末の画面へ反映する（送信は間引くが、表示は間引かない）。
 */
export const SELF_SOURCE = 'gv-self';
/**
 * 自機マーカーの色。他機の色（LIVE_COLORS）に青系が無いので、
 * 「現在地」の定番色である青にして見分けやすくする。
 * 白い縁取りの丸（gv-self-halo）を下に敷くので、どの地図配色でも視認できる。
 */
const SELF_COLOR = '#1f6fff';

/**
 * 自機位置の手動入力の仮の点（ドラッグして微調整できる）。
 * ★ 最初は`maplibregl.Marker`（DOM要素・`draggable`）で実装したが、実機
 *   （iOS Safari）で座標は正しく設定されるのに地図上に点が描画されない
 *   不具合が確認された（原因未特定・このサンドボックスでは再現できず）。
 *   このアプリの他の地物（自機位置ほか全て）はGeoJSONソース+circleレイヤーで
 *   描いており実機で確認済みの方式なので、Markerを諦めてそちらに合わせた。
 *   ドラッグは`setupLongPress`と同じ流儀（コンテナへの生ポインタイベント＋
 *   `map.unproject`）で自前実装する（main.ts参照）。
 */
export const MANUAL_POSITION_SOURCE = 'gv-manual-position';
/**
 * 計測モードで描いた図形（直線・円・多角形）。「消す」まで残り、
 * 表示/非表示は別に切り替えられる（data/measure.ts と main.ts の setupMeasure 参照）。
 */
export const MEASURE_SOURCE = 'gv-measure';
/**
 * タスクシート（TaskBoard）でタップしたターゲットの位置に出すカーソル
 * （十字の輪＋名前・座標、MMAがあれば実寸の円）。docs/08 §194
 */
export const TASKBOARD_CURSOR_SOURCE = 'gv-taskboard-cursor';
/**
 * プランナー（LRN/3DT）の入力点（開始点・円の中心・リング中心・進入点）。
 * タップで置き、常にドラッグで微調整できる（main.ts の setupLrn3dt 参照）。
 */
export const LRN3DT_POINT_SOURCE = 'gv-lrn3dt-point';
/** 自機（青）と紛れない、「未確定・要調整」を示すオレンジ寄りの色 */
const MANUAL_POSITION_COLOR = '#ffb300';
/**
 * 計測の図形の色。既存のどの地物とも被らない黄緑にする
 * （空域の緑#2ecc71・高速の緑#34c759より明るく、SAの黄#ffd400とも別）。
 */
const MEASURE_COLOR = '#b6ff3a';

/** PZ=朱色、SA=黄色。どちらも半透明で塗り、輪郭だけ濃くする（区域そのものの色は変えない） */
const PZSA_COLORS: Record<PzSaZone['type'], string> = { PZ: '#e2451c', SA: '#ffd400' };
/** PZ/SAの3D表示（円柱・角柱・tubeの筒）の不透明度。すべて同じ見え方に揃える */
const PZSA_3D_OPACITY = 0.35;
/**
 * ラベル文字だけの色。PZの朱色は文字だと漢字が潰れて読みにくいとの指摘があり、
 * 薄い桃色にしている（区域の塗り・輪郭の色＝PZSA_COLORSとは別に持つ）。
 * UTM方眼のラベルにも同じ色を使い、見た目を揃える
 */
const PZ_LABEL_COLOR = '#ffadd6';
const LABEL_COLORS: Record<PzSaZone['type'], string> = { PZ: PZ_LABEL_COLOR, SA: '#ffd400' };

/**
 * 鉛直断面ビューの断面線の色。BluePZ（青）・PZ/SA・航跡の高度配色の
 * どれとも被らない、切断面を示す中立のオレンジにする。
 */
const SECTION_COLOR = '#ff9f1c';
/** LRN/3DTプランナーの推奨ルート描画色。他の常設レイヤーと衝突しない紫 */
const LRN3DT_PLAN_COLOR = '#9b5cff';
/** 選択中ゴールへの点線の色。他の常設レイヤーと衝突しないシアン */
const GOAL_BEARING_COLOR = '#00e5ff';

/**
 * 3DT 設定空域の色。BluePZ（禁止＝青）と取り違えると危険なので、
 * 「入りたい空間」であることが分かる緑系にする。
 * 鉛直断面ビューでも同じ色を使うため公開する。
 */
export const AIRSPACE_COLOR = '#2ecc71';

/**
 * BluePZの色。既存の青系（自機 SELF_COLOR '#1f6fff'、宣言ゴールの
 * '#4aa8ff'、高度配色ランプの '#4361ee'/'#4cc9f0'）と見分けがつくよう
 * シアン寄りに寄せてある。実機・3D表示で視認確認して必要なら調整する。
 *
 * 地図以外の画面（鉛直断面ビュー）でも同じ色でBluePZを描くため公開する。
 * 同じものが画面ごとに違う色になると、対応関係が読めなくなるため。
 */
export const NOTAM_COLOR = '#2979ff';

/**
 * 飛行場中心の参考円（NOTAM_REF_SOURCE）の色。BluePZ本体の青とは
 * はっきり区別できる、警告色ではない中立のグレーにする
 * （高度制限ではなく参考情報であることが見た目からも分かるように）。
 */
const NOTAM_REF_COLOR = '#9aa5b1';

/**
 * ライブ表示の端末色のパレット。端末IDのハッシュから自動で割り振る既定色であり、
 * 「端末ごとの色を設定」画面で選べる選択肢でもある（main.ts から参照するため export）
 */
export const LIVE_COLORS = ['#00d9a3', '#ffd23f', '#ff8c42', '#4cc9f0', '#c77dff', '#ff6b9d', '#7bd389'];

/**
 * 端末IDごとに手動で選んだ色の上書き。「端末ごとの色を設定」画面（main.ts）から
 * setTerminalColorOverrides() で差し替える。ここに無ければ従来通りハッシュから自動で決める
 */
const terminalColorOverrides = new Map<string, string>();

export function setTerminalColorOverrides(overrides: Map<string, string>): void {
  terminalColorOverrides.clear();
  for (const [id, color] of overrides) terminalColorOverrides.set(id, color);
}

export function colorForTerminal(terminalId: string): string {
  const override = terminalColorOverrides.get(terminalId);
  if (override) return override;
  let h = 0;
  for (let i = 0; i < terminalId.length; i++) h = (h * 31 + terminalId.charCodeAt(i)) >>> 0;
  return LIVE_COLORS[h % LIVE_COLORS.length];
}

/**
 * 高度の色分けに使う配色。黄・橙・赤は地理院地図（淡色）の道路（国道・県道）と
 * 同系色で見分けが付かなくなるため、ここでは使わない。緑〜青〜紫〜赤紫の
 * 寒色〜中間色でまとめ、地図の道路・建物の淡い色とは明確に区別できる
 * 彩度の高い配色にしてある。
 */
const ALTITUDE_COLORS = ['#00d9a3', '#00b4d8', '#4cc9f0', '#4361ee', '#7209b7', '#f72585'];

/** 高度の刻み幅の既定値（フィート）。会場ごとに高度制限が異なるため変えられるようにしてある */
export const DEFAULT_ALTITUDE_STEP_FT = 500;

/** 高度の色分けの境界値。表示用のft（切りの良い数値）と、航跡データ（メートル）と
 *  比較するためのmを両方持つ */
export interface AltitudeStop {
  ft: number;
  m: number;
  color: string;
}

/**
 * 高度の色分けの境界値を、フィートの切りの良い刻み幅で作る。
 * メートル側の境界値を先に決めてftへ変換すると 656ft/1312ft…という半端な
 * 数値になるため、境界値はフィート側で先に決め、航跡データと比べるための
 * メートル値はそこから逆算する。
 */
export function buildAltitudeStops(stepFeet: number = DEFAULT_ALTITUDE_STEP_FT): AltitudeStop[] {
  return ALTITUDE_COLORS.map((color, i) => {
    const ft = i * stepFeet;
    return { ft, m: feetToMeters(ft), color };
  });
}

/** アプリ起動直後、会場が決まるまでの既定の色分け */
export const ALTITUDE_STOPS: AltitudeStop[] = buildAltitudeStops();

/**
 * 高度[m]に対応する色を JS 側で求める。
 *
 * `altitudeColorExpression` は MapLibre の式なので地図レイヤーにしか使えない。
 * ウィンドプロファイラのように自前で描く画面でも**同じ配色**にするために、
 * 同じ線形補間をここで行う。2つが離れると配色がずれるので、必ず隣に置いておく。
 */
export function colorForAltitude(altM: number, stops: AltitudeStop[]): string {
  if (stops.length === 0) return '#ffffff';
  if (altM <= stops[0].m) return stops[0].color;
  const last = stops[stops.length - 1];
  if (altM >= last.m) return last.color;

  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (altM >= a.m && altM <= b.m) {
      const ratio = b.m === a.m ? 0 : (altM - a.m) / (b.m - a.m);
      return mixHexColor(a.color, b.color, ratio);
    }
  }
  return last.color;
}

/**
 * `colorForAltitude`に、`altitudeColorExpression`と同じ対地(AGL)/海抜(MSL)
 * 切り替えを適用したもの。RidgeLineLayer（カスタムWebGLレイヤー）は
 * MapLibreの式を使えないため、頂点ごとの色をJS側で求める必要がある
 * （main.tsのupdateRidgeLines()から呼ぶ）。
 */
export function colorForAltitudeMode(
  altM: number,
  stops: AltitudeStop[],
  mode: AltitudeMode = 'msl',
  groundRefM = 0,
): string {
  return colorForAltitude(mode === 'agl' ? altM - groundRefM : altM, stops);
}

/** #rrggbb を RidgeLineLayer が使う [r,g,b,a]（各0〜1）へ変換する */
export function hexToRgba01(hex: string, alpha = 1): [number, number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16) / 255,
    Number.parseInt(hex.slice(3, 5), 16) / 255,
    Number.parseInt(hex.slice(5, 7), 16) / 255,
    alpha,
  ];
}

/** #rrggbb 同士を ratio(0〜1) で混ぜる */
function mixHexColor(from: string, to: string, ratio: number): string {
  const parse = (hex: string): [number, number, number] => [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  ];
  const [r1, g1, b1] = parse(from);
  const [r2, g2, b2] = parse(to);
  const ch = (a: number, b: number) =>
    Math.round(a + (b - a) * ratio)
      .toString(16)
      .padStart(2, '0');
  return `#${ch(r1, r2)}${ch(g1, g2)}${ch(b1, b2)}`;
}

/**
 * 高度の色分けの基準。
 * 'msl' … 海抜高度（今までの既定。alt プロパティをそのまま使う）
 * 'agl' … 対地高度（altから基準標高groundRefMを一律に差し引く）
 *
 * ★ 標高の高い会場（例: 会場自体が海抜2000ft超）では、海抜基準だと低空
 *   飛行中でも色分けの上限にすぐ張り付いて一色になってしまう、との指摘
 *   （2026-09-13）。
 * ★ 当初は地形データ（§136のgroundM）をフィーチャーごとに引く実装
 *   だったが、利用者から「基準標高を表示して、入力で直せるように」との
 *   要望（同日）があり、地点ごとの自動査定ではなく**会場につき1つの
 *   基準標高**を利用者が確認・修正できる値にする形に変更した
 *   （実際の競技運用でもQNH同様、単一の基準値で運用するのが自然）。
 *   `groundRefM`は会場の地形標高（例: 打ち上げ地点）をmain.ts側で
 *   `map.queryTerrainElevation()`から自動提案し、以後は利用者の入力を
 *   優先する。
 */
export type AltitudeMode = 'msl' | 'agl';

/** 航跡ライン用の高度→色の補間式。会場ごとに刻み幅が変わるので都度組み立てる */
export function altitudeColorExpression(
  stops: AltitudeStop[],
  mode: AltitudeMode = 'msl',
  groundRefM = 0,
): ExpressionSpecification {
  const input =
    mode === 'agl'
      ? (['-', ['get', 'alt'], groundRefM] as unknown as ExpressionSpecification)
      : (['get', 'alt'] as unknown as ExpressionSpecification);
  return [
    'interpolate',
    ['linear'],
    input,
    ...stops.flatMap((s) => [s.m, s.color]),
  ] as unknown as ExpressionSpecification;
}

/**
 * ライブ航跡の色の付け方（docs/08 §218）。
 * - `altitude`: 高度の色分け（既定。保存済みフライトと見比べられる）
 * - `terminal`: 機体ごとの単色（現在位置のアイコン・ラベルと同じ端末色）。航跡が重なってきたとき、
 *   高度の色分けのままだと同じ高度の線どうしが同じ色で重なり、自分のチームの航跡が見分けられなくなるため
 */
export type LiveTrackColorMode = 'altitude' | 'terminal';

let liveTrackColorMode: LiveTrackColorMode = 'altitude';

/** 切り替える。レイヤーへの反映は`applyAltitudeColors`（または作り直し時のレイヤー定義）が読む */
export function setLiveTrackColorMode(mode: LiveTrackColorMode): void {
  liveTrackColorMode = mode;
}

export function getLiveTrackColorMode(): LiveTrackColorMode {
  return liveTrackColorMode;
}

/**
 * ライブ航跡（2Dの線・3Dの壁）の色の式。`terminal`のときは各フィーチャの`color`
 * （`liveTracksToGeoJson`・`liveTracks3dToGeoJson`が端末色を入れている）をそのまま使う
 */
function liveTrackColorExpression(altitudeExpr: ExpressionSpecification): ExpressionSpecification {
  return liveTrackColorMode === 'terminal' ? (['get', 'color'] as unknown as ExpressionSpecification) : altitudeExpr;
}

/**
 * ライブ航跡の光彩（白い縁取り、docs/08 §232）。航跡が重なっても、選んだ機体（★のお気に入りなど）の線が
 * 地図の細かい線・他機の線から浮いて見えるようにする。色は白、幅は線の両側に約3px、ほぼ不透明
 * （利用者が示したおすすめ: 光彩の色 白・サイズ3〜5pt・透明度0〜20%）。
 * - `off`: 付けない ／ `favorites`: ★の機体だけ ／ `all`: すべての機体
 */
export type LiveTrackGlowMode = 'off' | 'favorites' | 'all';

let liveTrackGlowMode: LiveTrackGlowMode = 'favorites';
let liveTrackGlowFavorites: string[] = [];

function liveTrackGlowFilter(): FilterSpecification {
  return liveTrackGlowMode === 'all'
    ? (['has', 'terminalId'] as FilterSpecification)
    : (['in', ['get', 'terminalId'], ['literal', liveTrackGlowFavorites]] as FilterSpecification);
}

/** 光彩の付け方とお気に入りを覚え、地図があればすぐ反映する（レイヤーの作り直し時は定義がここの値を読む） */
export function setLiveTrackGlow(map: MapLibreMap | null, mode: LiveTrackGlowMode, favorites: Iterable<string>): void {
  liveTrackGlowMode = mode;
  liveTrackGlowFavorites = [...favorites];
  if (!map?.getLayer('gv-live-line-glow')) return;
  map.setLayoutProperty('gv-live-line-glow', 'visibility', mode === 'off' ? 'none' : 'visible');
  map.setFilter('gv-live-line-glow', liveTrackGlowFilter());
}

/** 地図の再スタイル後などに、現在の刻み幅・基準・基準標高を航跡レイヤーへ反映し直す */
export function applyAltitudeColors(
  map: MapLibreMap,
  stops: AltitudeStop[],
  mode: AltitudeMode = 'msl',
  groundRefM = 0,
): void {
  const expr = altitudeColorExpression(stops, mode, groundRefM);
  if (map.getLayer('gv-track-line')) map.setPaintProperty('gv-track-line', 'line-color', expr);
  // ライブ航跡だけは、高度の色分けと機体ごとの単色を切り替えられる（保存済みフライトは常に高度の色分け）
  const liveExpr = liveTrackColorExpression(expr);
  if (map.getLayer('gv-live-line')) map.setPaintProperty('gv-live-line', 'line-color', liveExpr);
  if (map.getLayer('gv-track-3d-wall')) map.setPaintProperty('gv-track-3d-wall', 'fill-extrusion-color', expr);
  if (map.getLayer('gv-live-track-3d-wall')) {
    map.setPaintProperty('gv-live-track-3d-wall', 'fill-extrusion-color', liveExpr);
  }
}

/** 3D航跡の高さの誇張倍率。既定10倍（会場の高度上限2500ft≒760mだと等倍では地図上でほぼ平らに見えるため） */
export const DEFAULT_HEIGHT_SCALE = 10;

/**
 * `alt`・`floorM`・`ceilingM`・`altM`はいずれも海抜高度[m]の絶対値。
 * 3D地形（`setTerrain()`）を有効にすると、maplibre-gl-jsは`fill-extrusion`の
 * base・heightの両方に、その地点の地形標高を**自動的に加算する**
 * （node_modules/maplibre-glのシェーダーソースで確認済み、2026-09-12）。
 * そのままだと地形の標高ぶんが2重に乗ってしまうため、絶対高度を使う式は
 * すべてここを通して地形標高（`groundM`。地形が無効・未取得の会場では
 * フィーチャに乗らないので`coalesce`で0扱いになり、今までの式と完全に
 * 一致する）を先に差し引いてから誇張倍率を掛けること。
 */
function aboveGround(prop: string): ExpressionSpecification {
  return ['-', ['get', prop], ['coalesce', ['get', 'groundM'], 0]] as unknown as ExpressionSpecification;
}

/**
 * 高度[m]（altitudeBandsの`alt`プロパティ、地形標高差し引き後）に誇張倍率を
 * 掛けたfill-extrusion-height式。hazardRoadLevelFilter と同じ要領で、
 * 実行時の値を都度expressionへ焼き込む。
 * ['max', ..., 0] は、fill-extrusion-base（0）を下回れない制約への保険
 * （まれなGPS異常値で負の高度が来ても地図が壊れないようにする）。
 */
export function heightScaleExpression(scale: number): ExpressionSpecification {
  return ['*', ['max', aboveGround('alt'), 0], scale] as unknown as ExpressionSpecification;
}

/** 高さの誇張倍率を変更したときに呼ぶ。ジオメトリは変えず、paintプロパティだけ更新する（軽量） */
export function applyHeightScale(map: MapLibreMap, scale: number): void {
  const expr = heightScaleExpression(scale);
  if (map.getLayer('gv-track-3d-wall')) map.setPaintProperty('gv-track-3d-wall', 'fill-extrusion-height', expr);
  if (map.getLayer('gv-live-track-3d-wall')) {
    map.setPaintProperty('gv-live-track-3d-wall', 'fill-extrusion-height', expr);
  }
  if (map.getLayer('gv-live-point-3d-arrow')) {
    map.setPaintProperty('gv-live-point-3d-arrow', 'fill-extrusion-height', expr);
  }
  if (map.getLayer('gv-goal-3d-pin')) map.setPaintProperty('gv-goal-3d-pin', 'fill-extrusion-height', expr);

  // 柱の先端の球は、柱と同じ誇張倍率で「位置」だけ追従させる（球自体の大きさは変えない）
  const ballBase = ballBaseExpression(scale);
  const ballTop = ballTopExpression(scale);
  if (map.getLayer('gv-goal-3d-ball')) {
    map.setPaintProperty('gv-goal-3d-ball', 'fill-extrusion-base', ballBase);
    map.setPaintProperty('gv-goal-3d-ball', 'fill-extrusion-height', ballTop);
  }
  if (map.getLayer('gv-live-point-3d-ball')) {
    map.setPaintProperty('gv-live-point-3d-ball', 'fill-extrusion-base', ballBase);
    map.setPaintProperty('gv-live-point-3d-ball', 'fill-extrusion-height', ballTop);
  }

  if (map.getLayer('gv-notam-3d-cap')) {
    map.setPaintProperty('gv-notam-3d-cap', 'fill-extrusion-base', notamCapBaseExpression(scale));
    map.setPaintProperty('gv-notam-3d-cap', 'fill-extrusion-height', notamCapTopExpression(scale));
  }
  if (map.getLayer('gv-airspace-3d')) {
    map.setPaintProperty('gv-airspace-3d', 'fill-extrusion-base', airspaceFloorExpression(scale));
    map.setPaintProperty('gv-airspace-3d', 'fill-extrusion-height', airspaceCeilingExpression(scale));
  }
  if (map.getLayer('gv-pzsa-3d')) {
    map.setPaintProperty('gv-pzsa-3d', 'fill-extrusion-height', pzsaCeilingExpression(scale));
  }
  if (map.getLayer('gv-pzsa-poly-3d')) {
    map.setPaintProperty('gv-pzsa-poly-3d', 'fill-extrusion-height', pzsaCeilingExpression(scale));
  }
  if (map.getLayer('gv-lrn3dt-plan-3d-wall')) {
    map.setPaintProperty('gv-lrn3dt-plan-3d-wall', 'fill-extrusion-base', planRibbonBaseExpression(scale));
    map.setPaintProperty('gv-lrn3dt-plan-3d-wall', 'fill-extrusion-height', planRibbonTopExpression(scale));
  }
  if (map.getLayer('gv-lrn3dt-plan-3d-volume')) {
    map.setPaintProperty('gv-lrn3dt-plan-3d-volume', 'fill-extrusion-base', airspaceFloorExpression(scale));
    map.setPaintProperty('gv-lrn3dt-plan-3d-volume', 'fill-extrusion-height', airspaceCeilingExpression(scale));
  }
}

/**
 * NOTAM区域（BluePZ）は「地面からの柱」ではなく「上空に浮かぶ薄い板」として
 * 見せたいという要望（"青い面を上空に表示"）に合わせ、ゴール球
 * （ballBaseExpression/ballTopExpression）と同じ「base/topを両方式にする」
 * パターンで、実効天井の高さを中心に薄い板を浮かせる。誇張倍率が変わっても
 * 薄さの見た目が変わらないよう、厚みはメートル換算後（＝誇張後）の定数で持つ。
 */
const NOTAM_CAP_THICKNESS_M = 15;
function notamCapBaseExpression(scale: number): ExpressionSpecification {
  return [
    '-',
    ['*', ['max', aboveGround('alt'), 0], scale],
    NOTAM_CAP_THICKNESS_M / 2,
  ] as unknown as ExpressionSpecification;
}
/**
 * 3DT設定空域の3D表示。下限〜上限を丸ごと埋めた箱にする（NOTAMの「天井の
 * 薄板」と違い、空域は中身のある空間なので）。
 * `floorM`/`ceilingM` はフィーチャ側で決めた実数値[m]（制限なしは
 * 0 と十分高い値に置き換えて渡す）。
 */
function airspaceFloorExpression(scale: number): ExpressionSpecification {
  return ['*', ['max', aboveGround('floorM'), 0], scale] as unknown as ExpressionSpecification;
}
function airspaceCeilingExpression(scale: number): ExpressionSpecification {
  return ['*', ['max', aboveGround('ceilingM'), 0], scale] as unknown as ExpressionSpecification;
}

/**
 * PZ/SAの3D表示（円筒）。禁止・要注意区域は「地表から高度上限までの空間
 * まるごとが対象」なので、NOTAMの薄板ではなく3DT空域と同じ「床0・天井altM」の
 * 柱にする（利用者の要望どおり）。高度上限が無い区域（altM: null）は
 * 高さ0（＝実質非表示）にして、誤って無制限の柱を立てないようにする。
 */
function pzsaCeilingExpression(scale: number): ExpressionSpecification {
  return [
    '*',
    ['max', ['-', ['coalesce', ['get', 'altM'], 0], ['coalesce', ['get', 'groundM'], 0]], 0],
    scale,
  ] as unknown as ExpressionSpecification;
}

/**
 * プランナーの推奨航跡を3Dで描くときの、浮かぶリボンの厚み [m] と幅 [m]。
 *
 * ★ 地面から高度まで「壁」を立てないこと。脚は数kmあり高度も数百mあるので、
 *   誇張倍率を掛けると**数km×数kmの不透明な面**になり、地図が一切読めなく
 *   なる（実測。倍率10倍で画面がほぼ一色になった）。NOTAMの「上空に浮かぶ
 *   薄板」と同じく、その高度の位置に薄いリボンを浮かせる。
 * ★ 幅は航跡の壁（4m）より広く取る。壁と違って上から見る面なので、
 *   細いと遠景でほとんど見えない。
 */
const PLAN_3D_RIBBON_THICKNESS_M = 25;
export const PLAN_3D_RIBBON_WIDTH_M = 90;

function planRibbonBaseExpression(scale: number): ExpressionSpecification {
  return [
    '-',
    ['*', ['max', aboveGround('alt'), 0], scale],
    PLAN_3D_RIBBON_THICKNESS_M / 2,
  ] as unknown as ExpressionSpecification;
}
function planRibbonTopExpression(scale: number): ExpressionSpecification {
  return [
    '+',
    ['*', ['max', aboveGround('alt'), 0], scale],
    PLAN_3D_RIBBON_THICKNESS_M / 2,
  ] as unknown as ExpressionSpecification;
}

function notamCapTopExpression(scale: number): ExpressionSpecification {
  return [
    '+',
    ['*', ['max', aboveGround('alt'), 0], scale],
    NOTAM_CAP_THICKNESS_M / 2,
  ] as unknown as ExpressionSpecification;
}

/**
 * 航跡を高度帯ごとの連続線に切り出す。
 *
 * 1点ずつの短い線分にすると、ベクタタイル化の際の簡略化で微小な線が落ち、
 * 航跡が破線のように途切れてしまう。同じ高度帯が続くかぎり1本の線にまとめ、
 * 帯の変わり目では次の点を共有させて隙間を作らない。
 *
 * bandSize は色分けの見た目の刻み幅（ALTITUDE_STOPS）とは別物で、線を
 * 繋ぐための技術的な粒度（細かいほど滑らかだが、細切れになりやすい）。
 */
function altitudeBands(fixes: FixSample[], bandSize: number): GeoJSON.Feature[] {
  const features: GeoJSON.Feature[] = [];
  const altOf = (f: FixSample) => f.altPressure ?? f.altMsl ?? 0;

  let coords: Array<[number, number]> = [];
  let band = NaN;
  let bandAltSum = 0;

  const flush = () => {
    if (coords.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { alt: bandAltSum / coords.length, band },
      });
    }
  };

  for (const f of fixes) {
    const alt = altOf(f);
    const b = Math.round(alt / bandSize) * bandSize;
    if (coords.length === 0) {
      band = b;
      bandAltSum = 0;
    } else if (b !== band) {
      // 変わり目の点は両方の線に含めて連続させる
      coords.push([f.lon, f.lat]);
      flush();
      coords = [coords[coords.length - 1]];
      band = b;
      bandAltSum = 0;
    }
    coords.push([f.lon, f.lat]);
    bandAltSum += alt;
  }
  flush();

  return features;
}

export function trackToGeoJson(fixes: FixSample[], bandSize = 50): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: altitudeBands(fixes, bandSize) };
}

/**
 * 3D航跡（fill-extrusion）の壁の幅 [m]。
 * 個々の点に柱を立てる方式だと、実寸幅は広域ズームでは潰れて見えなくなる
 * （fill-extrusionの形状はズームに応じて拡大されない）。航跡に沿って
 * 隣接点をつなぐ「薄い壁」にすれば、壁の長さ自体が航跡の実距離なので
 * どのズームでも見え続ける。太さは調整対象にせず固定値にしておく。
 */
const TRACK_3D_WIDTH_M = 4;

/** 緯度 lat における、経度1度・緯度1度あたりのメートル数 */
function metersPerDegree(lat: number): { lon: number; lat: number } {
  return { lon: 111320 * Math.cos((lat * Math.PI) / 180), lat: 110574 };
}

/**
 * 中心＋半径[m]から、実際の緯度経度で閉じた円のリングを作る。
 *
 * ★ 円は必ずこれで「地物」として描くこと。circleレイヤーの`circle-radius`に
 *   メートル→ピクセルの近似式を入れる方法は**使わない**。
 *   以前は competitionエリア・PZ/SA・タスクサークル・3DTリングを
 *   `['interpolate', ['exponential', 2], ['zoom'], 10, r/100, 18, r/0.4]`
 *   で描いていたが、これは特定のズームでしか合わず、実機（iPhone）の
 *   スクリーンショットを実測したところ **半径1,200mのSAが0.73km、
 *   1,000mのLRN円が0.62kmと、いずれも約0.61倍に縮んで**描かれていた。
 *   PZ（禁止区域）が実際より小さく見えるのは危険側の誤りなので、
 *   ズームに依存しないポリゴンへ統一した。
 */
export function geoCircleRing(
  lat: number,
  lon: number,
  radiusM: number,
  sides = 72,
): [number, number][] {
  const { lon: mPerLon, lat: mPerLat } = metersPerDegree(lat);
  const ring: [number, number][] = [];
  for (let i = 0; i <= sides; i++) {
    const theta = (i / sides) * Math.PI * 2;
    ring.push([lon + (Math.cos(theta) * radiusM) / mPerLon, lat + (Math.sin(theta) * radiusM) / mPerLat]);
  }
  return ring;
}

/**
 * 隣接する2点(lon1,lat1)-(lon2,lat2)から、幅widthMの薄い壁ポリゴンの座標
 * （進行方向に垂直な法線ベクトルぶんだけ左右にオフセットした閉じた4点+終点）
 * を作る。停止中など同座標が続く区間（0幅になる）は null を返す。
 */
function wallQuadCoords(
  lon1: number,
  lat1: number,
  lon2: number,
  lat2: number,
  widthM: number,
): [number, number][] | null {
  const halfWidth = widthM / 2;
  const { lon: mPerLon, lat: mPerLat } = metersPerDegree((lat1 + lat2) / 2);
  const dxM = (lon2 - lon1) * mPerLon;
  const dyM = (lat2 - lat1) * mPerLat;
  const lengthM = Math.hypot(dxM, dyM);
  if (lengthM < 0.01) return null; // 停止中など同座標が続く区間は0幅ポリゴンになるので飛ばす

  // 進行方向 (dxM, dyM) に垂直な単位ベクトル
  const nx = -dyM / lengthM;
  const ny = dxM / lengthM;
  const offLon = (nx * halfWidth) / mPerLon;
  const offLat = (ny * halfWidth) / mPerLat;

  return [
    [lon1 + offLon, lat1 + offLat],
    [lon2 + offLon, lat2 + offLat],
    [lon2 - offLon, lat2 - offLat],
    [lon1 - offLon, lat1 - offLat],
    [lon1 + offLon, lat1 + offLat],
  ];
}

/**
 * 高度帯ごとのLineString1本を、幅widthMの薄い壁ポリゴン列（隣接2点ごとに1枚）に
 * 変換する。fill-extrusionはポリゴンの底面積ぶんを一様な高さで押し上げる
 * だけなので、線のまま押し上げても厚み0で描画されない。
 */
function lineToWallPolygons(feature: GeoJSON.Feature, widthM: number): GeoJSON.Feature[] {
  const coords = (feature.geometry as GeoJSON.LineString).coordinates as [number, number][];
  const polygons: GeoJSON.Feature[] = [];

  for (let i = 0; i < coords.length - 1; i++) {
    const [lon1, lat1] = coords[i];
    const [lon2, lat2] = coords[i + 1];
    const quad = wallQuadCoords(lon1, lat1, lon2, lat2, widthM);
    if (!quad) continue;
    polygons.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [quad] },
      properties: feature.properties,
    });
  }
  return polygons;
}

/**
 * 経緯度のLineString列を、3Dの壁ポリゴンに変換して返す。
 *
 * `properties.alt`（メートル）をそのまま引き継ぐので、
 * `heightScaleExpression`（`['get','alt']` を使う）がそのまま効く。
 * プランナーの推奨航跡を3Dで描くために main.ts から呼ぶ
 * （UTM→経緯度の変換は main.ts の担当、という既存の役割分担のため、
 * ここでは既に経緯度になった線を受け取る）。
 */
export function linesToWallPolygons(
  features: GeoJSON.Feature[],
  widthM = TRACK_3D_WIDTH_M,
): GeoJSON.Feature[] {
  return features.flatMap((f) =>
    f.geometry.type === 'LineString' ? lineToWallPolygons(f, widthM) : [],
  );
}

/**
 * 3D航跡の壁を「滑らか」に作る。`altitudeBands` の帯分け（既定50m刻み）を
 * 経由せず、隣接する2点ごとにその区間の実測高度（2点の平均）をそのまま
 * ポリゴンの高さにする。fill-extrusionは1枚のポリゴンを一定の高さでしか
 * 押し上げられず天面を傾けられないが、区間を細かく（=生データ点そのまま）
 * 保つことで階段の一段が視認できないほど小さくなり、見た目には滑らかになる。
 * `alt`プロパティの意味は帯版（`altitudeBands`）と同じ「区間の平均高度」
 * なので、色・誇張倍率の式（`['get','alt']`を使う）は無変更で両対応する。
 */
function smoothWallPolygons(fixes: FixSample[], widthM: number): GeoJSON.Feature[] {
  const altOf = (f: FixSample) => f.altPressure ?? f.altMsl ?? 0;
  const polygons: GeoJSON.Feature[] = [];
  for (let i = 0; i < fixes.length - 1; i++) {
    const a = fixes[i];
    const b = fixes[i + 1];
    const quad = wallQuadCoords(a.lon, a.lat, b.lon, b.lat, widthM);
    if (!quad) continue;
    polygons.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [quad] },
      properties: { alt: (altOf(a) + altOf(b)) / 2 },
    });
  }
  return polygons;
}

/**
 * 3Dの壁・稜線用に点を間引く。水平に`minStepM`以上進んだか、高度が
 * `minAltStepM`以上変わった点だけを残す（最初と最後の点は必ず残す）。
 *
 * ★ なぜ要るか（2026-10-10、競技中に「3D表示がとても重くソフトがハング
 *   アップする。航跡1本なら耐えるが複数になると重い」と指摘、docs/08 §195）。
 *   1秒1点の航跡を1区間＝1枚の壁にしていたため、7機×1時間で約2万5千枚
 *   （GeoJSONで約9MB）になり、それをライブ更新のたびに作り直して地図へ
 *   渡し直していた。気球は毎秒数mしか進まず、数mおきの壁は3Dで見ても
 *   区別がつかないので、見た目を変えずに枚数を数分の1にできる。
 */
export const THREE_D_MIN_STEP_M = 25;
export const THREE_D_MIN_ALT_STEP_M = 5;

/**
 * 3Dの壁・稜線の細かさ（間引きの刻み）。
 *
 * ★ 端末ごとに変えられるようにした（2026-10-10、利用者から「刻みは
 *   マシンスペックによって許容度が変わる。PCでは細かく、スマホでは荒く
 *   設定できるとよい」、docs/08 §197）。既定は §195 で決めた 25m / 5m。
 *   好みとして端末に保存する（main.ts の loadThreeDDetail）。
 */
export interface ThreeDDetail {
  /** 水平の刻み [m] */
  stepM: number;
  /** 高さの刻み [m] */
  altStepM: number;
}

export const DEFAULT_THREE_D_DETAIL: ThreeDDetail = { stepM: THREE_D_MIN_STEP_M, altStepM: THREE_D_MIN_ALT_STEP_M };

/** 設定画面の選択肢。「カスタム」は数値を直接入れたとき */
export const THREE_D_DETAIL_PRESETS: ReadonlyArray<{ id: string; label: string; detail: ThreeDDetail }> = [
  { id: 'fine', label: '細かい・PC向け', detail: { stepM: 10, altStepM: 2 } },
  { id: 'normal', label: '標準', detail: DEFAULT_THREE_D_DETAIL },
  { id: 'coarse', label: '荒い・スマホ向け', detail: { stepM: 50, altStepM: 10 } },
  { id: 'coarsest', label: 'とても荒い・古いスマホ向け', detail: { stepM: 100, altStepM: 20 } },
];

export const THREE_D_STEP_RANGE = { min: 1, max: 200 } as const;
export const THREE_D_ALT_STEP_RANGE = { min: 1, max: 50 } as const;

/** 刻みの値を範囲内に収める（数でなければ既定値）。保存値・入力値の両方に使う */
export function normalizeThreeDDetail(raw: unknown): ThreeDDetail {
  const r = (raw ?? {}) as Partial<Record<keyof ThreeDDetail, unknown>>;
  const clamp = (v: unknown, range: { min: number; max: number }, fallback: number): number => {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
    return Number.isFinite(n) ? Math.min(range.max, Math.max(range.min, n)) : fallback;
  };
  return {
    stepM: clamp(r.stepM, THREE_D_STEP_RANGE, DEFAULT_THREE_D_DETAIL.stepM),
    altStepM: clamp(r.altStepM, THREE_D_ALT_STEP_RANGE, DEFAULT_THREE_D_DETAIL.altStepM),
  };
}

/** 刻みに一致する選択肢のid。どれにも一致しなければ 'custom' */
export function threeDDetailPresetId(d: ThreeDDetail): string {
  return THREE_D_DETAIL_PRESETS.find((p) => p.detail.stepM === d.stepM && p.detail.altStepM === d.altStepM)?.id ?? 'custom';
}

export function simplifyFixesFor3d<T extends { lat: number; lon: number; altPressure?: number | null; altMsl: number | null }>(
  fixes: T[],
  minStepM = THREE_D_MIN_STEP_M,
  minAltStepM = THREE_D_MIN_ALT_STEP_M,
): T[] {
  if (fixes.length <= 2) return fixes;
  const altOf = (f: T) => f.altPressure ?? f.altMsl ?? 0;
  const out: T[] = [fixes[0]];
  let last = fixes[0];
  const minStep2 = minStepM * minStepM;
  for (let i = 1; i < fixes.length - 1; i++) {
    const f = fixes[i];
    const { lon: mPerLon, lat: mPerLat } = metersPerDegree(f.lat);
    const dx = (f.lon - last.lon) * mPerLon;
    const dy = (f.lat - last.lat) * mPerLat;
    if (dx * dx + dy * dy >= minStep2 || Math.abs(altOf(f) - altOf(last)) >= minAltStepM) {
      out.push(f);
      last = f;
    }
  }
  out.push(fixes[fixes.length - 1]);
  return out;
}

/**
 * 保存済みフライトの3D航跡（高度に応じた壁）。実際の高さはfill-extrusion-heightで掛け算する。
 * `smooth=true`なら`bandSize`は使わず、区間ごとの実測高度で壁を作る（見た目が滑らかになる）。
 */
export function track3dToGeoJson(
  fixes: FixSample[],
  bandSize = 50,
  smooth = false,
  detail: ThreeDDetail = DEFAULT_THREE_D_DETAIL,
): GeoJSON.FeatureCollection {
  const pts = simplifyFixesFor3d(fixes, detail.stepM, detail.altStepM);
  const features = smooth
    ? smoothWallPolygons(pts, TRACK_3D_WIDTH_M)
    : altitudeBands(pts, bandSize).flatMap((f) => lineToWallPolygons(f, TRACK_3D_WIDTH_M));
  return { type: 'FeatureCollection', features };
}

/** ライブ受信中の各機の3D航跡。2D版（liveTracksToGeoJson）と同じ端末色を使う */
export function liveTracks3dToGeoJson(
  terminals: Array<{ terminalId: string; fixes: FixSample[] }>,
  bandSize = 50,
  smooth = false,
  detail: ThreeDDetail = DEFAULT_THREE_D_DETAIL,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: terminals
      .filter((t) => t.fixes.length >= 2)
      .flatMap((t) => {
        const color = colorForTerminal(t.terminalId);
        const pts = simplifyFixesFor3d(t.fixes, detail.stepM, detail.altStepM);
        if (smooth) {
          return smoothWallPolygons(pts, TRACK_3D_WIDTH_M).map((f) => ({
            ...f,
            properties: { ...f.properties, terminalId: t.terminalId, color },
          }));
        }
        return altitudeBands(pts, bandSize).flatMap((f) =>
          lineToWallPolygons(
            { ...f, properties: { ...f.properties, terminalId: t.terminalId, color } },
            TRACK_3D_WIDTH_M,
          ),
        );
      }),
  };
}

/** ライブ矢印（3D）の柱の全長・後端の幅 [m] */
const LIVE_ARROW_LENGTH_M = 6;
const LIVE_ARROW_WIDTH_M = 4;

/**
 * 進行方向 headingDeg（真方位、0=北・時計回り）を向いた小さな三角形を
 * (lon, lat) を中心に作る。ローカル座標（+yが真北）で先端・後端2点を
 * 定義してから heading ぶん回転させ、metersPerDegree で緯度経度へ変換する。
 */
function headingArrowPolygon(lon: number, lat: number, headingDeg: number): [number, number][] {
  const { lon: mPerLon, lat: mPerLat } = metersPerDegree(lat);
  const rad = (headingDeg * Math.PI) / 180;
  const half = LIVE_ARROW_WIDTH_M / 2;
  const local: [number, number][] = [
    [0, LIVE_ARROW_LENGTH_M * 0.6], // 先端
    [half, -LIVE_ARROW_LENGTH_M * 0.4], // 右後
    [-half, -LIVE_ARROW_LENGTH_M * 0.4], // 左後
  ];
  const ring = local.map(([x, y]) => {
    const rx = x * Math.cos(rad) + y * Math.sin(rad);
    const ry = -x * Math.sin(rad) + y * Math.cos(rad);
    return [lon + rx / mPerLon, lat + ry / mPerLat] as [number, number];
  });
  return [...ring, ring[0]];
}

/**
 * ライブ端末の最新位置を、進行方向を向いた矢印型の柱として表す（3D表示用）。
 * symbolレイヤーには高度を持たせる機能が無いため、fill-extrusionの
 * 柱で代用する。地面に張り付いた従来の2D矢印（gv-live-point）とは
 * 独立したレイヤーで、両方同時に表示される。
 */
export function livePointArrow3dToGeoJson(
  terminals: Array<{ terminalId: string; latest: FixSample }>,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: terminals.map((t) => ({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [headingArrowPolygon(t.latest.lon, t.latest.lat, t.latest.heading ?? 0)],
      },
      properties: {
        terminalId: t.terminalId,
        color: colorForTerminal(t.terminalId),
        alt: t.latest.altPressure ?? t.latest.altMsl ?? 0,
      },
    })),
  };
}

/** ゴールの高度ピン（3D）の半径 [m] */
const GOAL_PIN_RADIUS_M = 2.5;

/** (lon, lat) を中心にした半径radiusMの8角形リング（小さな円の近似） */
function pointToPinPolygon(lon: number, lat: number, radiusM: number): [number, number][] {
  const { lon: mPerLon, lat: mPerLat } = metersPerDegree(lat);
  const sides = 8;
  const ring: [number, number][] = [];
  for (let i = 0; i <= sides; i++) {
    const theta = (i / sides) * Math.PI * 2;
    ring.push([lon + (Math.cos(theta) * radiusM) / mPerLon, lat + (Math.sin(theta) * radiusM) / mPerLat]);
  }
  return ring;
}

/**
 * 高度（altFt）が設定された宣言ゴールだけを、その高度ぶんの柱として表す
 * （3D表示用）。地上へ下ろす垂線の役割は柱の側面が果たす。
 */
export function declaredGoals3dToGeoJson(
  goals: Array<{ key: string; lat: number; lon: number; altFt: number | null }>,
  selectedKey: string | null,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: goals
      .filter((g) => g.altFt != null)
      .map((g) => ({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [pointToPinPolygon(g.lon, g.lat, GOAL_PIN_RADIUS_M)] },
        properties: { alt: feetToMeters(g.altFt as number), selected: g.key === selectedKey },
      })),
  };
}

/**
 * 柱の先端に置く「球」の半径・高さ [m]。
 * 誇張倍率（heightScale）は柱の高さ（位置）にだけかかり、球自体の大きさは
 * 固定にする——倍率を上げるほど球が巨大化すると見た目がおかしくなるため。
 */
const BALL_RADIUS_M = 5;
const BALL_HEIGHT_M = 8;

/** alt・誇張倍率から、球の下端の高さexpressionを作る（柱の先端を球の中心が来るよう包む） */
function ballBaseExpression(scale: number): ExpressionSpecification {
  return ['-', ['*', ['max', aboveGround('alt'), 0], scale], BALL_HEIGHT_M / 2] as unknown as ExpressionSpecification;
}
/** 同、球の上端 */
function ballTopExpression(scale: number): ExpressionSpecification {
  return ['+', ['*', ['max', aboveGround('alt'), 0], scale], BALL_HEIGHT_M / 2] as unknown as ExpressionSpecification;
}

/** (lon, lat, alt) + 追加propertiesから、球1個ぶんのFeatureを作る共通ヘルパー */
function ballFeature(lon: number, lat: number, alt: number, properties: GeoJSON.GeoJsonProperties): GeoJSON.Feature {
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [pointToPinPolygon(lon, lat, BALL_RADIUS_M)] },
    properties: { ...properties, alt },
  };
}

/** ライブ矢印の柱の先端に置く球。端末色は柱と同じ */
export function livePointBall3dToGeoJson(
  terminals: Array<{ terminalId: string; latest: FixSample }>,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: terminals.map((t) =>
      ballFeature(t.latest.lon, t.latest.lat, t.latest.altPressure ?? t.latest.altMsl ?? 0, {
        terminalId: t.terminalId,
        color: colorForTerminal(t.terminalId),
      }),
    ),
  };
}

/** ゴールの高度ピンの先端に置く球。色は柱（declaredGoals3dToGeoJson）と同じ選択有無で決める */
export function declaredGoalBalls3dToGeoJson(
  goals: Array<{ key: string; lat: number; lon: number; altFt: number | null }>,
  selectedKey: string | null,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: goals
      .filter((g) => g.altFt != null)
      .map((g) => ballFeature(g.lon, g.lat, feetToMeters(g.altFt as number), { selected: g.key === selectedKey })),
  };
}

/** 離陸点・着陸点 */
export function endpointsToGeoJson(fixes: FixSample[]): GeoJSON.FeatureCollection {
  if (fixes.length === 0) return { type: 'FeatureCollection', features: [] };
  const first = fixes[0];
  const last = fixes[fixes.length - 1];
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [first.lon, first.lat] },
        properties: { kind: 'launch', label: '離陸' },
      },
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [last.lon, last.lat] },
        properties: { kind: 'landing', label: '着陸' },
      },
    ],
  };
}

/**
 * 会場のゴール地点。
 *
 * `declaredEights` に載っている点（＝一覧に入れたゴール）は `declared:true`
 * を持たせ、`gv-goal-label` のフィルタで名称を出さないようにする。
 * その代わり `gv-declared-label` が、輪の大きさに合わせた位置に出す
 * （小さい点向けの既定オフセットだと、宣言の輪に文字が食われるため）。
 */
export function goalsToGeoJson(
  goals: MarkPoint[],
  declaredEights?: Set<string>,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: goals.map((g) => {
      const u = toUtm(g.lon, g.lat);
      // 表記トグルの4パターン（4/4・5/4・4/5・5/5）どれで宣言されていても光る
      const declared = [
        toGridCode(u.easting, u.northing, false, false),
        toGridCode(u.easting, u.northing, true, false),
        toGridCode(u.easting, u.northing, false, true),
        toGridCode(u.easting, u.northing, true, true),
      ].some((c) => declaredEights?.has(c) ?? false);
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [g.lon, g.lat] },
        properties: { name: g.name, radius: g.radius ?? 0, declared },
      };
    }),
  };
}

/** 宣言したゴール。`selected` が付いた1点だけが赤い輪で強調される */
export function declaredToGeoJson(
  goals: Array<{ key: string; name: string; lat: number; lon: number }>,
  selectedKey: string | null,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: goals.map((g) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [g.lon, g.lat] },
      properties: { name: g.name, key: g.key, selected: g.key === selectedKey },
    })),
  };
}

/** 競技エリア（円）。TV の $_TVCIRCLE 由来のデータをそのまま描ける */
export function areasToGeoJson(areas: CircleArea[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = areas.map((a) => ({
    type: 'Feature',
    // 実際の緯度経度で円を描く（geoCircleRingのコメント参照。circle-radiusの
    // ズーム近似では実機で約0.61倍に縮んでいた）
    geometry: { type: 'Polygon', coordinates: [geoCircleRing(a.lat, a.lon, a.radius)] },
    properties: { group: a.group, id: a.id, radius: a.radius, color: a.color ?? '#ff00aa' },
  }));
  return { type: 'FeatureCollection', features };
}

/**
 * 地図上のラベル文字列。「PZ36」の行と「馬 800ft」の行に分けて、種別+番号・
 * kind（目印の通称）・高度制限をひとまとめにする（従来はkindだけ、または
 * label と kind の組み合わせだけで、どの区域かひと目で分からなかった）。
 * 横長になって読みにくいとの指摘があったため2行にしている。
 * label が「No.36」のような形式でも数字だけ拾って詰める。
 */
function pzSaDisplayLabel(z: PzSaZone): string {
  const num = z.label.match(/\d+/)?.[0] ?? z.label;
  const line1 = `${z.type}${num}`;
  const line2 = [z.kind, z.altFt != null ? `${z.altFt}ft` : null].filter(Boolean).join(' ');
  return line2 ? `${line1}\n${line2}` : line1;
}

/** PZ/SA（円）。半径・種類・備考をタップ詳細で読めるよう properties にそのまま残す */
export function pzSaToGeoJson(zones: PzSaZone[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = zones
    .filter((z): z is PzSaCircleZone => z.shape === 'circle')
    .map((z) => ({
      type: 'Feature',
      // ★ 実際の緯度経度で円を描く。PZ（禁止区域）が実際より小さく描かれるのは
      //   危険側の誤りなので、ズーム近似は使わない（geoCircleRing参照）
      geometry: { type: 'Polygon', coordinates: [geoCircleRing(z.lat, z.lon, z.radiusM)] },
      properties: {
        id: z.id,
        pzsaType: z.type,
        label: z.label,
        dispLabel: pzSaDisplayLabel(z),
        radius: z.radiusM,
        altFt: z.altFt,
        // 3D表示（fill-extrusion）用。高度上限が無い区域はnullのままにし、
        // 式側でnullを0m扱いにして誤って高い柱を立てないようにする
        altM: z.altFt != null ? feetToMeters(z.altFt) : null,
        kind: z.kind,
        note: z.note,
        color: PZSA_COLORS[z.type],
      },
    }));
  return { type: 'FeatureCollection', features };
}

/**
 * tube（中心線＋半径）を帯状ポリゴンへ変換する。隣接2点ごとに
 * wallQuadCoords で幅2*radiusMの四角形を作るだけ（track3dの壁と同じ
 * 要領。折れ点でのマイター処理はしないので、急な折れ角では四角形どうしに
 * 多少のすき間・重なりが出るが、見た目には問題にならない簡易実装とする）。
 *
 * これは2D（塗り・輪郭・ラベル）用の、幅いっぱいの帯1枚。3D表示は半円の
 * 断面の筒（tube-mesh.ts・tube-layer.ts）で別に描く（このポリゴンをそのまま
 * 押し上げると平らな天面の角柱になってしまうため）。筒の縁はこの帯と同じ
 * 式（metersPerDegree）で計算しているので、両端では一致する（tube-mesh.test.ts）。
 * 折れ点では、こちらは区間ごとの四角形、筒は前後の向きの平均で断面を作るため
 * わずかに異なる。
 */
function tubeToPolygonCoords(z: PzSaTubeZone): Array<[number, number][]> {
  const widthM = z.radiusM * 2;
  const rings: Array<[number, number][]> = [];
  for (let i = 0; i < z.centerline.length - 1; i++) {
    const a = z.centerline[i];
    const b = z.centerline[i + 1];
    const quad = wallQuadCoords(a.lon, a.lat, b.lon, b.lat, widthM);
    if (quad) rings.push(quad);
  }
  return rings;
}

/**
 * tube（線状PZ）の3D表示（`pzsaTubeLayer`）のメッシュを、今のPZ/SAデータから
 * 作り直す。表示しないとき（3D OFF・PZ/SA非表示）は空の配列を渡せば消える。
 * 色・不透明度は他のPZ/SAの3D表示（gv-pzsa-3d等）と揃える。
 */
export function updatePzSaTubeMesh(
  zones: PzSaZone[],
  options: { heightScale: number; groundElevation: (lon: number, lat: number) => number },
): void {
  const tubes: TubeMeshInput[] = zones
    .filter((z): z is PzSaTubeZone => z.shape === 'tube' && z.altFt != null)
    .map((z) => {
      const [r, g, b] = hexToRgb01(PZSA_COLORS[z.type]);
      return {
        centerline: z.centerline,
        radiusM: z.radiusM,
        peakAltM: feetToMeters(z.altFt!),
        color: [r, g, b, PZSA_3D_OPACITY],
      };
    });
  pzsaTubeLayer.setMesh(buildTubeMesh(tubes, options));
}

function hexToRgb01(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16);
  return [((v >> 16) & 0xff) / 255, ((v >> 8) & 0xff) / 255, (v & 0xff) / 255];
}

/**
 * PZ/SA（矩形など、原典が「地図参照」としている多角形。および
 * tube＝中心線＋半径で表す線状の構造物）。
 * 円と違って半径だけでは表せないので、頂点をそのまま線で結んで描く。
 */
export function pzSaPolygonsToGeoJson(zones: PzSaZone[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const z of zones) {
    if (z.shape === 'polygon' && z.points.length >= 3) {
      const ring = z.points.map((p) => [p.lon, p.lat]);
      ring.push(ring[0]);
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [ring] },
        properties: {
          id: z.id,
          pzsaType: z.type,
          label: z.label,
          dispLabel: pzSaDisplayLabel(z),
          altFt: z.altFt,
          altM: z.altFt != null ? feetToMeters(z.altFt) : null,
          kind: z.kind,
          note: z.note,
          color: PZSA_COLORS[z.type],
        },
      });
    } else if (z.shape === 'tube' && z.centerline.length >= 2) {
      for (const ring of tubeToPolygonCoords(z)) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [ring] },
          properties: {
            id: z.id,
            // ★ gv-pzsa-poly-3dがtubeを除外するためのフラグ（3Dはこちらの
            //   平らな帯ではなく、半円の筒＝pzsaTubeLayer側で描く）
            shape: 'tube',
            pzsaType: z.type,
            label: z.label,
            dispLabel: pzSaDisplayLabel(z),
            altFt: z.altFt,
            altM: z.altFt != null ? feetToMeters(z.altFt) : null,
            kind: z.kind,
            note: z.note,
            color: PZSA_COLORS[z.type],
          },
        });
      }
    }
  }
  return { type: 'FeatureCollection', features };
}

/**
 * NOTAM高度制限区域（BluePZ）。現在有効な体制(regime)のゾーンだけを
 * ポリゴン化する。activeOverrideIdsで有効化された条件付き高度引き上げが
 * あれば、effectiveCeilingFt（@gv4d/core）で実効天井を求めてalt(m)に反映する。
 */
/**
 * 3DT 設定空域を地図のソースへ。
 *
 * 3D表示（fill-extrusion）は式で `floorM`/`ceilingM` を読むので、
 * 「制限なし（null）」はここで実数へ置き換えておく（式の中で null を
 * 扱おうとすると分岐が読みにくくなるため）。上限なしの場合は、実運用の
 * 高度をはるかに超える値を入れて「天井が見えない箱」として描く。
 */
const AIRSPACE_OPEN_CEILING_M = 6000;

export function airspaceToGeoJson(
  volumes: ReadonlyArray<{
    label: string;
    points: Array<{ lat: number; lon: number }>;
    floorFt: number | null;
    ceilingFt: number | null;
  }>,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: volumes
      .filter((v) => v.points.length >= 3)
      .map((v) => {
        const ring = v.points.map((p) => [p.lon, p.lat] as [number, number]);
        ring.push(ring[0]); // 描画のため始点へ戻す（データ側は開いた頂点列）
        return {
          type: 'Feature' as const,
          properties: {
            label: v.label,
            floorFt: v.floorFt,
            ceilingFt: v.ceilingFt,
            floorM: v.floorFt == null ? 0 : feetToMeters(v.floorFt),
            ceilingM:
              v.ceilingFt == null ? AIRSPACE_OPEN_CEILING_M : feetToMeters(v.ceilingFt),
          },
          geometry: { type: 'Polygon' as const, coordinates: [ring] },
        };
      }),
  };
}

export function notamToGeoJson(
  regime: NotamRegime | null,
  activeOverrideIds: ReadonlySet<string>,
): GeoJSON.FeatureCollection {
  if (!regime) return { type: 'FeatureCollection', features: [] };
  const features: GeoJSON.Feature[] = regime.zones
    .filter((z) => z.points.length >= 3)
    .map((z) => {
      const ring = z.points.map((p) => [p.lon, p.lat]);
      ring.push(ring[0]);
      const effectiveAltFt = effectiveCeilingFt(z, regime.id, activeOverrideIds);
      return {
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [ring] },
        properties: {
          id: z.id,
          label: z.label,
          altFt: z.altFt,
          effectiveAltFt,
          regimeLabel: regime.label,
          note: z.note,
          topologyNote: z.topologyNote ?? '',
          color: NOTAM_COLOR,
          alt: feetToMeters(effectiveAltFt),
        },
      };
    });
  return { type: 'FeatureCollection', features };
}

/** 1海里 [m] */
const METERS_PER_NM = 1852;

/**
 * 飛行場中心の参考円（宇都宮飛行場5NM/10NM/15NMなど）。BluePZの高度制限
 * ゾーンとは異なり実寸が数十kmに及ぶため、pointToPinPolygonより頂点数を
 * 増やして輪郭を滑らかにする。塗りは持たず、線（破線）としてだけ使う。
 */
export function notamReferenceCirclesToGeoJson(
  circles: NotamReferenceCircle[],
): GeoJSON.FeatureCollection {
  const sides = 72;
  return {
    type: 'FeatureCollection',
    features: circles.map((c) => {
      const radiusM = c.radiusNm * METERS_PER_NM;
      const { lon: mPerLon, lat: mPerLat } = metersPerDegree(c.center.lat);
      const ring: [number, number][] = [];
      for (let i = 0; i <= sides; i++) {
        const theta = (i / sides) * Math.PI * 2;
        ring.push([
          c.center.lon + (Math.cos(theta) * radiusM) / mPerLon,
          c.center.lat + (Math.sin(theta) * radiusM) / mPerLat,
        ]);
      }
      return {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: ring },
        properties: { label: c.label, color: NOTAM_REF_COLOR },
      };
    }),
  };
}

/**
 * ライブ受信中の各機の航跡（高度帯ごとの連続線。保存済みフライトの表示と揃える）。
 * terminalId と端末色も properties に残すので、将来ホバーやフィルタで使える。
 */
export function liveTracksToGeoJson(
  terminals: Array<{ terminalId: string; fixes: FixSample[] }>,
  bandSize = 50,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: terminals
      .filter((t) => t.fixes.length >= 2)
      .flatMap((t) =>
        altitudeBands(t.fixes, bandSize).map((f) => ({
          ...f,
          properties: {
            ...f.properties,
            terminalId: t.terminalId,
            color: colorForTerminal(t.terminalId),
          },
        })),
      ),
  };
}

/**
 * 端末IDラベルの表示位置（text-offset、em単位）。
 *
 * ゴールのラベルと同じく基本は**上側**。ただし進行方向がおおむね北だと、
 * 機体アイコンと到着予測の扇（gv-live-predict）がどちらも上へ伸びて
 * ラベルと重なってしまうため、その場合だけ下側に逃がす。
 */
function labelOffsetFor(headingDeg: number | null): [number, number] {
  const mag = 2.2; // em。矢印アイコンの見た目より十分外側に出す
  if (headingDeg == null) return [0, -mag];
  const h = ((headingDeg % 360) + 360) % 360;
  const facingNorth = h > 315 || h < 45;
  return facingNorth ? [0, mag] : [0, -mag];
}

/** ライブ受信中の各機の現在位置 */
export function livePointsToGeoJson(
  terminals: Array<{ terminalId: string; latest: FixSample }>,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: terminals.map((t) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [t.latest.lon, t.latest.lat] },
      properties: {
        terminalId: t.terminalId,
        color: colorForTerminal(t.terminalId),
        altFt: Math.round(metersToFeet(t.latest.altPressure ?? t.latest.altMsl ?? 0)),
        speedKmh: t.latest.speed == null ? null : Math.round(t.latest.speed * 3.6 * 10) / 10,
        heading: t.latest.heading == null ? null : Math.round(t.latest.heading),
        t: t.latest.t,
        labelOffset: labelOffsetFor(t.latest.heading),
      },
    })),
  };
}

/**
 * 他機が宣言したゴール（$_TVMARK 由来のMarkPoint）。
 * 原典GVでは○（丸）で表示される。端末ごとの色（colorForTerminal）を
 * そのまま使い、その機の航跡・現在位置ラベルと同じ色で見分けられるようにする
 */
export function liveMarksToGeoJson(marks: MarkPoint[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: marks.map((m) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [m.lon, m.lat] },
      properties: {
        name: m.name,
        terminalId: m.terminalId ?? '',
        color: colorForTerminal(m.terminalId ?? m.name),
      },
    })),
  };
}

/** タスクシートでタップしたターゲットのカーソル。null なら空 */
export function taskboardCursorToGeoJson(
  cursor: { lat: number; lon: number; label: string; color: string; radiusM: number | null } | null,
): GeoJSON.FeatureCollection {
  if (!cursor) return { type: 'FeatureCollection', features: [] };
  const features: GeoJSON.Feature[] = [
    {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [cursor.lon, cursor.lat] },
      properties: { role: 'point', label: cursor.label, color: cursor.color },
    },
  ];
  if (cursor.radiusM && cursor.radiusM > 0) {
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: geoCircleRing(cursor.lat, cursor.lon, cursor.radiusM) },
      properties: { role: 'mma', color: cursor.color },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** 自機位置（ローカルGPS）。無ければ空。他機と見分けやすいよう白い矢印で描く */
export function selfToGeoJson(
  fix: { lat: number; lon: number; heading: number | null } | null,
): GeoJSON.FeatureCollection {
  if (!fix) return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [fix.lon, fix.lat] },
        properties: { heading: fix.heading == null ? null : Math.round(fix.heading) },
      },
    ],
  };
}

/** 自機位置の手動入力の仮の点。selfToGeoJson と同じ形（向きを持たないので properties は空） */
export function manualPositionToGeoJson(
  coords: { lat: number; lon: number } | null,
): GeoJSON.FeatureCollection {
  if (!coords) return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: [
      { type: 'Feature', geometry: { type: 'Point', coordinates: [coords.lon, coords.lat] }, properties: {} },
    ],
  };
}

/**
 * プランナーの入力点。`label`はどの点か（開始点・円の中心 等）を示す文字で、
 * 同時に複数出ることがある（3DTはリング中心＋進入点の2点）ため付ける。
 */
export function lrn3dtPickPointsToGeoJson(
  points: readonly { field: string; label: string; lat: number; lon: number }[],
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: points.map((p) => ({
      type: 'Feature',
      properties: { field: p.field, label: p.label },
      geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
    })),
  };
}

/** 到着予測（30分）で振り返る直近の実測点数。原典GoalViewの10サンプルに合わせる */
const PREDICT_HISTORY = 10;
const PREDICT_MINUTES = 30;
const R_LAT_M = 110574;

/**
 * 「30分後にはどこにいるか」を、現在位置から直近の実測点それぞれの
 * 対地速度・方位のまま延長した細い線の束として表す。
 *
 * 原典GoalView（ノルデン爆撃照準器の応用）の「気球の接地地点の予測」に相当する。
 * 高度が変われば風向・風速が変わり、この線の向き・長さが動くので、
 * 見比べるだけで「高度による流され方の違い」が直感的にわかる。
 * 直近ほど太く不透明に、古いほど細く薄く描くための properties を付ける。
 */
export function livePredictToGeoJson(
  terminals: Array<{ terminalId: string; fixes: FixSample[] }>,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const t of terminals) {
    if (t.fixes.length === 0) continue;
    const current = t.fixes[t.fixes.length - 1];
    const color = colorForTerminal(t.terminalId);
    const rLon = 111320 * Math.cos((current.lat * Math.PI) / 180);
    const sample = t.fixes.slice(-PREDICT_HISTORY);

    sample.forEach((f, idx) => {
      if (f.speed == null || f.heading == null) return;
      const distanceM = f.speed * PREDICT_MINUTES * 60; // その時の対地速度のまま30分進んだと仮定した距離
      const rad = (f.heading * Math.PI) / 180;
      const lon = current.lon + (Math.sin(rad) * distanceM) / rLon;
      const lat = current.lat + (Math.cos(rad) * distanceM) / R_LAT_M;
      const age = sample.length - 1 - idx; // 0 = 最新

      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [current.lon, current.lat],
            [lon, lat],
          ],
        },
        properties: {
          terminalId: t.terminalId,
          color,
          latest: age === 0,
          opacity: age === 0 ? 0.9 : Math.max(0.1, 0.5 - age * 0.05),
        },
      });
    });
  }
  return { type: 'FeatureCollection', features };
}

const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

const ARROW_ICON = 'gv-arrow';

/**
 * ライブ端末の現在位置マーカー用の矢印アイコン。北（0度）向きの三角形を
 * SDF（符号無しの塗りマスクとして扱う簡易版）で作り、`icon-color` で
 * 端末ごとの色に、`icon-rotate` で進行方向に向ける。
 */
function createArrowIcon(size = 64): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.42;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.moveTo(cx, cy - r); // 先端（北向き）
  ctx.lineTo(cx + r * 0.62, cy + r * 0.8);
  ctx.lineTo(cx, cy + r * 0.35);
  ctx.lineTo(cx - r * 0.62, cy + r * 0.8);
  ctx.closePath();
  ctx.fill();
  return ctx.getImageData(0, 0, size, size);
}

/** 矢印アイコンをスタイルへ登録する。スタイル差し替えで消えるので毎回呼び直す */
function ensureArrowIcon(map: MapLibreMap): void {
  if (map.hasImage(ARROW_ICON)) return;
  map.addImage(ARROW_ICON, createArrowIcon(), { sdf: true, pixelRatio: 2 });
}

/**
 * タスクシートのカーソルの十字。画像の中心＝十字の中心なので、輪（circle
 * レイヤー）の中心にきっちり重なる。
 * ★ 以前は文字の「+」を重ねていたが、文字は書体のベースラインの分だけ
 *   下にずれ、輪の中心から下に外れて見えた（実機で指摘、docs/08 §194）。
 * 白い縁取り用（太い）と、ターゲットの色用（細い）の2枚を重ねる。
 */
const CROSS_ICON = 'gv-cross';
const CROSS_HALO_ICON = 'gv-cross-halo';

function createCrossIcon(lineWidth: number, size = 64): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  const arm = size * 0.34; // 輪（半径16px）の内側に収まる長さ（pixelRatio 2で約11px）
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(c - arm, c);
  ctx.lineTo(c + arm, c);
  ctx.moveTo(c, c - arm);
  ctx.lineTo(c, c + arm);
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

function ensureCrossIcons(map: MapLibreMap): void {
  if (!map.hasImage(CROSS_HALO_ICON)) map.addImage(CROSS_HALO_ICON, createCrossIcon(10), { sdf: true, pixelRatio: 2 });
  if (!map.hasImage(CROSS_ICON)) map.addImage(CROSS_ICON, createCrossIcon(5), { sdf: true, pixelRatio: 2 });
}

/**
 * 競技地図のraster source/layerを差し替える。タイルURL自体が会場ごとに
 * 違い、他のオーバーレイのように`addSource`後は`setData`で更新できない
 * （URLはsource作成時に焼き込まれる）ため、`addOverlayLayers`内の
 * 「無ければ足す」だけでは会場切り替えに追従できない。
 * **会場が変わるたびに呼び直すこと**（`main.ts`の会場切り替え処理から）。
 *
 * 表示ON/OFF・不透明度は毎回既定値にリセットされるので、呼び出し側で
 * `applyCompMapVisibility`/`applyCompMapOpacity`を続けて呼び、現在の
 * 設定を復元すること（`applyHeightScale`等と同じ「スタイル差し替え後に
 * 呼び直す」流儀）。
 */
export function setCompMapSource(map: MapLibreMap, config: CompMapLayerConfig | null): void {
  if (map.getLayer(COMPMAP_LAYER)) map.removeLayer(COMPMAP_LAYER);
  if (map.getSource(COMPMAP_SOURCE)) map.removeSource(COMPMAP_SOURCE);
  if (!config) return;

  const { tileUrlTemplate, manifest } = config;
  map.addSource(COMPMAP_SOURCE, {
    type: 'raster',
    tiles: [tileUrlTemplate],
    tileSize: manifest.tileSize,
    minzoom: manifest.minzoom,
    maxzoom: manifest.maxzoom,
    bounds: [manifest.bounds.west, manifest.bounds.south, manifest.bounds.east, manifest.bounds.north],
    attribution: manifest.attribution,
  });
  /*
   * ★ 必ず「既にあるGVのオーバーレイのうち最も下のもの」の直前へ入れること。
   *
   *   `addLayer` は挿入位置を指定しないと**最上段に積む**。この関数は
   *   `addOverlayLayers()` の中だけでなく、会場切り替え時に外からも
   *   呼び直される（タイルURLが会場ごとに違うため）ので、その場合は
   *   既にゴール・PZ/SA・航跡のレイヤーが存在する。位置を指定しないと
   *   競技地図がその上に乗り、**PZやゴールが競技地図に隠れてしまう**
   *   （実機で発覚、2026-08-29）。
   *
   *   `getStyle().layers` は下から順に並んでいるので、先頭に見つかった
   *   `gv-` レイヤーの前に入れれば、基図より上・他のオーバーレイより下になる。
   *   まだGVのレイヤーが1枚も無い場合（起動直後）は undefined になり、
   *   末尾に積まれる。その後に追加されるオーバーレイが上に載るので結果は同じ。
   */
  const firstOverlayId = map
    .getStyle()
    .layers?.find((l) => l.id.startsWith('gv-') && l.id !== COMPMAP_LAYER)?.id;

  map.addLayer(
    {
      id: COMPMAP_LAYER,
      type: 'raster',
      source: COMPMAP_SOURCE,
      // 既定オフ。表示ON/OFFは applyCompMapVisibility で別途反映する
      layout: { visibility: 'none' },
      paint: { 'raster-opacity': DEFAULT_COMPMAP_OPACITY, 'raster-fade-duration': 0 },
    },
    firstOverlayId,
  );
}

/** 競技地図オーバーレイの表示ON/OFFを切り替える */
export function applyCompMapVisibility(map: MapLibreMap, visible: boolean): void {
  if (map.getLayer(COMPMAP_LAYER)) {
    map.setLayoutProperty(COMPMAP_LAYER, 'visibility', visible ? 'visible' : 'none');
  }
}

/** 競技地図オーバーレイの不透明度を変更する */
export function applyCompMapOpacity(map: MapLibreMap, opacity: number): void {
  if (map.getLayer(COMPMAP_LAYER)) {
    map.setPaintProperty(COMPMAP_LAYER, 'raster-opacity', opacity);
  }
}

/**
 * 3D表示の地形（起伏）を設定する。`url`が`null`なら地形を無効化し、
 * 今まで通り平坦な地面に戻す（地形データが無い・ダウンロードしていない
 * 会場での既定動作）。
 *
 * ★ `setTerrain(null)`を先に呼んでからソースを消すこと。地形に使用中の
 *   ソースをそのまま`removeSource`すると、maplibre-gl-js側でエラーになる。
 * ★ `url`は`pmtiles://...`形式（オンラインはMapterhornの惑星全体PMTiles、
 *   オフラインは会場ぶんの事前ダウンロード済みファイル）。どちらを渡すかは
 *   呼び出し側（main.tsのapplyTerrainState）が判断する。
 */
export function setTerrainSource(map: MapLibreMap, url: string | null): void {
  map.setTerrain(null);
  if (map.getSource(TERRAIN_SOURCE)) map.removeSource(TERRAIN_SOURCE);
  if (!url) return;

  map.addSource(TERRAIN_SOURCE, {
    type: 'raster-dem',
    url,
    encoding: 'terrarium',
    tileSize: 512,
  });
  // 誇張倍率は1（実寸）にする。航跡・PZ等の高度誇張（heightScale、既定10倍）
  // とは別軸の設定で、地形自体は実際の縮尺のまま見せたいため
  map.setTerrain({ source: TERRAIN_SOURCE, exaggeration: 1 });
}

/** 描画レイヤーを地図へ追加する。スタイル差し替え後にも呼び直せる */
export function addOverlayLayers(map: MapLibreMap, compMap: CompMapLayerConfig | null = null): void {
  ensureArrowIcon(map);
  ensureCrossIcons(map);
  setCompMapSource(map, compMap);
  if (!map.getSource(TRACK_SOURCE)) {
    // tolerance を 0 にして、航跡の細かい形が簡略化で崩れないようにする
    map.addSource(TRACK_SOURCE, { type: 'geojson', data: empty, tolerance: 0, buffer: 128 });
  }
  if (!map.getSource(POINT_SOURCE)) {
    map.addSource(POINT_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(GOAL_SOURCE)) {
    map.addSource(GOAL_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(AREA_SOURCE)) {
    map.addSource(AREA_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(PZSA_SOURCE)) {
    map.addSource(PZSA_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(PZSA_POLY_SOURCE)) {
    map.addSource(PZSA_POLY_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(SKETCH_SOURCE)) {
    map.addSource(SKETCH_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(NOTAM_SOURCE)) {
    map.addSource(NOTAM_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(NOTAM_REF_SOURCE)) {
    map.addSource(NOTAM_REF_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(DECLARED_SOURCE)) {
    map.addSource(DECLARED_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(UTM_GRID_SOURCE)) {
    map.addSource(UTM_GRID_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(SECTION_SOURCE)) {
    map.addSource(SECTION_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(LRN3DT_PLAN_SOURCE)) {
    map.addSource(LRN3DT_PLAN_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(LRN3DT_PLAN_3D_SOURCE)) {
    map.addSource(LRN3DT_PLAN_3D_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(GOAL_BEARING_SOURCE)) {
    map.addSource(GOAL_BEARING_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(AIRSPACE_SOURCE)) {
    map.addSource(AIRSPACE_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(SELF_SOURCE)) {
    map.addSource(SELF_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(MANUAL_POSITION_SOURCE)) {
    map.addSource(MANUAL_POSITION_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(MEASURE_SOURCE)) {
    map.addSource(MEASURE_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(TASKBOARD_CURSOR_SOURCE)) {
    map.addSource(TASKBOARD_CURSOR_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(LRN3DT_POINT_SOURCE)) {
    map.addSource(LRN3DT_POINT_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(HAZARD_SOURCE)) {
    // OpenStreetMap由来のデータなので、ODbLの帰属表示義務を満たすため
    // attributionを付けておく（MapLibreの右下の帰属表示に出る）
    map.addSource(HAZARD_SOURCE, {
      type: 'geojson',
      data: empty,
      attribution: '© OpenStreetMap contributors',
    });
  }

  // 送電線・鉄道・道路。UTM方眼と同じく背景の目印なので、航跡・ゴールより下に描く
  if (!map.getLayer('gv-hazard-line')) {
    map.addLayer({
      id: 'gv-hazard-line',
      type: 'line',
      source: HAZARD_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': [
          'match',
          ['get', 'kind'],
          'power',
          HAZARD_COLORS.power,
          'rail',
          HAZARD_COLORS.rail,
          'arterial',
          HAZARD_COLORS.arterial,
          'expressway',
          HAZARD_COLORS.expressway,
          '#999999',
        ],
        'line-width': [
          'match',
          ['get', 'kind'],
          'power',
          HAZARD_WIDTHS.power,
          'rail',
          HAZARD_WIDTHS.rail,
          'arterial',
          HAZARD_WIDTHS.arterial,
          'expressway',
          HAZARD_WIDTHS.expressway,
          1.5,
        ],
        'line-opacity': 0.8,
      },
      filter: hazardRoadLevelFilter(DEFAULT_HAZARD_ROAD_LEVEL),
    });
  }

  // 鉄道。実線だと幹線道路（同じく実線・近い色味）と見分けづらいので破線にする
  if (!map.getLayer('gv-hazard-rail-line')) {
    map.addLayer({
      id: 'gv-hazard-rail-line',
      type: 'line',
      source: HAZARD_SOURCE,
      layout: { 'line-cap': 'butt', 'line-join': 'round' },
      paint: {
        'line-color': HAZARD_COLORS.rail,
        'line-width': HAZARD_WIDTHS.rail,
        'line-opacity': 0.8,
        'line-dasharray': [6, 6],
      },
      filter: ['==', ['get', 'kind'], 'rail'],
    });
  }

  /*
   * UTM方眼線。競技で位置を読むのに使う基準なので、他の要素の下に
   * （最初に）描く。ラベルは symbol-placement:'line' で線に沿って
   * 繰り返し置かれるので、どこまでパンしても読める。
   */
  if (!map.getLayer('gv-utm-grid-line')) {
    map.addLayer({
      id: 'gv-utm-grid-line',
      type: 'line',
      source: UTM_GRID_SOURCE,
      paint: {
        'line-color': '#ff3b6b',
        'line-width': 0.75,
        'line-opacity': 0.45,
      },
    });
  }

  // 3DT の設定空域。PZ/SA・NOTAMと同じ塗り+輪郭+ラベルの3枚構成に、
  // 下限〜上限を埋める3Dの箱を足したもの
  if (!map.getLayer('gv-airspace-fill')) {
    map.addLayer({
      id: 'gv-airspace-fill',
      type: 'fill',
      source: AIRSPACE_SOURCE,
      paint: { 'fill-color': AIRSPACE_COLOR, 'fill-opacity': 0.14 },
    });
  }
  if (!map.getLayer('gv-airspace-line')) {
    map.addLayer({
      id: 'gv-airspace-line',
      type: 'line',
      source: AIRSPACE_SOURCE,
      layout: { 'line-join': 'round' },
      paint: { 'line-color': AIRSPACE_COLOR, 'line-width': 2, 'line-opacity': 0.9 },
    });
  }
  if (!map.getLayer('gv-airspace-3d')) {
    map.addLayer({
      id: 'gv-airspace-3d',
      type: 'fill-extrusion',
      source: AIRSPACE_SOURCE,
      layout: { visibility: 'none' },
      paint: {
        'fill-extrusion-base': airspaceFloorExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-height': airspaceCeilingExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': AIRSPACE_COLOR,
        'fill-extrusion-opacity': 0.25,
      },
    });
  }

  // 鉛直断面ビューで切っている位置。奥行きの帯（薄い塗り）＋中心線（実線）で
  // 「どこを、どれだけの幅で切っているか」の両方が分かるようにする
  if (!map.getLayer('gv-section-band')) {
    map.addLayer({
      id: 'gv-section-band',
      type: 'fill',
      source: SECTION_SOURCE,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'fill-color': SECTION_COLOR,
        'fill-opacity': 0.12,
      },
    });
  }
  if (!map.getLayer('gv-section-line')) {
    map.addLayer({
      id: 'gv-section-line',
      type: 'line',
      source: SECTION_SOURCE,
      filter: ['==', ['geometry-type'], 'LineString'],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': SECTION_COLOR,
        'line-width': 2,
        'line-opacity': 0.9,
      },
    });
  }
  // LRN/3DTプランナーの推奨ルート。面（LRNの得点になる三角形）＋線（脚／
  // 3DT区間）＋頂点（開始・脱出・進入点・中心点）の3枚構成で、
  // gv-section-band/lineと同じ考え方。面は線より先に追加して下に敷く
  if (!map.getLayer('gv-lrn3dt-plan-fill')) {
    map.addLayer({
      id: 'gv-lrn3dt-plan-fill',
      type: 'fill',
      source: LRN3DT_PLAN_SOURCE,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'fill-color': LRN3DT_PLAN_COLOR,
        // 成績になる面積を示すだけの塗り。下の地図が読めなくならない濃さにする
        'fill-opacity': 0.18,
      },
    });
  }
  if (!map.getLayer('gv-lrn3dt-plan-line')) {
    map.addLayer({
      id: 'gv-lrn3dt-plan-line',
      type: 'line',
      source: LRN3DT_PLAN_SOURCE,
      // 3DTの推奨航跡のうち「得点になる区間」は下の専用レイヤーで実線にするので、
      // ここでは除く（LRNの脚は scored を持たないので従来どおりここで描かれる）
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['!=', ['get', 'scored'], true]],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': LRN3DT_PLAN_COLOR,
        'line-width': 3,
        'line-opacity': 0.85,
        'line-dasharray': [2, 1.5],
      },
    });
  }
  /*
   * 3DTの推奨航跡のうち、リング内を飛べていて距離に加算される区間。
   * `line-dasharray` はデータ駆動にできない（式を受け付けない）ので、
   * 破線・実線の描き分けはレイヤーを分けるしかない。
   * 実線かつ少し太くして、「結果の距離をどこで稼いでいるか」が
   * ひと目で分かるようにする。
   */
  if (!map.getLayer('gv-lrn3dt-plan-line-scored')) {
    map.addLayer({
      id: 'gv-lrn3dt-plan-line-scored',
      type: 'line',
      source: LRN3DT_PLAN_SOURCE,
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 'scored'], true]],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': LRN3DT_PLAN_COLOR,
        'line-width': 4.5,
        'line-opacity': 0.95,
      },
    });
  }
  if (!map.getLayer('gv-lrn3dt-plan-point')) {
    map.addLayer({
      id: 'gv-lrn3dt-plan-point',
      type: 'circle',
      source: LRN3DT_PLAN_SOURCE,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        // 計算に使った中心点（role=center）は、ルートの頂点と区別できるよう
        // 中を白抜きにする（実機で「どれが中心か分からない」を避ける）
        'circle-color': ['case', ['==', ['get', 'role'], 'center'], '#ffffff', LRN3DT_PLAN_COLOR],
        'circle-radius': ['case', ['==', ['get', 'role'], 'center'], 4, 5],
        'circle-stroke-color': ['case', ['==', ['get', 'role'], 'center'], LRN3DT_PLAN_COLOR, '#ffffff'],
        'circle-stroke-width': ['case', ['==', ['get', 'role'], 'center'], 2.5, 1.5],
      },
    });
  }

  /*
   * プランナーの3D表示。2Dの線・面と同じ計算結果を、高度のある形で描く。
   * ★ 3D表示がOFFの間はデータ自体を空にする（main.ts側）ので、ここでは
   *   visibility を切り替えず常に visible にしてある。航跡の3Dレイヤーと
   *   同じ流儀（データで出し分ける）。
   */
  if (!map.getLayer('gv-lrn3dt-plan-3d-volume')) {
    map.addLayer({
      id: 'gv-lrn3dt-plan-3d-volume',
      type: 'fill-extrusion',
      source: LRN3DT_PLAN_3D_SOURCE,
      filter: ['==', ['get', 'kind'], 'volume'],
      paint: {
        // 3DTのリング（高度帯を埋めた環）とLRNの得点三角形（薄い板）。
        // 空域の3D表示と同じ base/ceiling 方式
        'fill-extrusion-base': airspaceFloorExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-height': airspaceCeilingExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': LRN3DT_PLAN_COLOR,
        // ★ 濃くしないこと。3DTのリングは半径数kmの筒になるため、
        //   誇張倍率を上げるとカメラが中に入り、濃いと地図が読めなくなる
        'fill-extrusion-opacity': 0.12,
      },
    });
  }
  if (!map.getLayer('gv-lrn3dt-plan-3d-wall')) {
    map.addLayer({
      id: 'gv-lrn3dt-plan-3d-wall',
      type: 'fill-extrusion',
      source: LRN3DT_PLAN_3D_SOURCE,
      filter: ['==', ['get', 'kind'], 'wall'],
      paint: {
        'fill-extrusion-base': planRibbonBaseExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-height': planRibbonTopExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': LRN3DT_PLAN_COLOR,
        'fill-extrusion-opacity': 0.9,
      },
    });
  }
  if (!map.getLayer('gv-utm-grid-label')) {
    map.addLayer({
      id: 'gv-utm-grid-label',
      type: 'symbol',
      source: UTM_GRID_SOURCE,
      // 拡大しすぎると密になりすぎるので、少し引いたところから出す
      minzoom: 11,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 220,
        'text-field': ['get', 'label'],
        'text-size': 13,
        'text-font': ['Noto Sans Regular'],
        'text-keep-upright': true,
      },
      paint: {
        'text-color': PZ_LABEL_COLOR,
        'text-halo-color': '#0b1119',
        'text-halo-width': 1.2,
      },
    });
  }

  // 競技エリア（円）。半径はメートルなので、緯度に応じた画素数へ換算する
  // 競技エリアの円。ポリゴンなので傾けても地面に沿い、ズームに依らず実寸で出る
  // （以前のcircleレイヤー＋circle-radiusのズーム近似は約0.61倍に縮んでいた）
  if (!map.getLayer('gv-area-fill')) {
    map.addLayer({
      id: 'gv-area-fill',
      type: 'fill',
      source: AREA_SOURCE,
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.12,
      },
    });
  }
  if (!map.getLayer('gv-area-line')) {
    map.addLayer({
      id: 'gv-area-line',
      type: 'line',
      source: AREA_SOURCE,
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 1.5,
        'line-opacity': 0.8,
      },
    });
  }

  /*
   * PZ（禁止・要注意区域、朱色）/ SA（要注意エリア、黄色）。
   * どちらも飛行中つねに意識すべき地上の危険・要配慮箇所なので、
   * 競技エリア（gv-area-fill）に準じた半透明の円で常時表示する。
   */
  if (!map.getLayer('gv-pzsa-fill')) {
    map.addLayer({
      id: 'gv-pzsa-fill',
      type: 'fill',
      source: PZSA_SOURCE,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.22,
      },
    });
  }
  if (!map.getLayer('gv-pzsa-circle-line')) {
    map.addLayer({
      id: 'gv-pzsa-circle-line',
      type: 'line',
      source: PZSA_SOURCE,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 1.5,
        'line-opacity': 0.85,
      },
    });
  }
  if (!map.getLayer('gv-pzsa-label')) {
    map.addLayer({
      id: 'gv-pzsa-label',
      type: 'symbol',
      source: PZSA_SOURCE,
      minzoom: 12,
      layout: {
        'text-field': ['get', 'dispLabel'],
        'text-size': 13,
        'text-font': ['Noto Sans Regular'],
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': ['match', ['get', 'pzsaType'], 'PZ', LABEL_COLORS.PZ, LABEL_COLORS.SA],
        'text-halo-color': '#0b1119',
        'text-halo-width': 1.2,
      },
    });
  }
  // 3D表示（円筒）。禁止・要注意区域は地表から高度上限までの空間が対象なので、
  // NOTAMの薄板ではなく3DT空域と同じ「床0・天井」の柱で表す（利用者の要望）
  if (!map.getLayer('gv-pzsa-3d')) {
    map.addLayer({
      id: 'gv-pzsa-3d',
      type: 'fill-extrusion',
      source: PZSA_SOURCE,
      filter: ['==', ['geometry-type'], 'Polygon'],
      layout: { visibility: 'none' },
      paint: {
        'fill-extrusion-base': 0,
        'fill-extrusion-height': pzsaCeilingExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': ['get', 'color'],
        'fill-extrusion-opacity': PZSA_3D_OPACITY,
      },
    });
  }

  // 円で表せない区域（矩形など、原典の「地図参照」）。輪郭線をはっきり見せる
  if (!map.getLayer('gv-pzsa-poly-fill')) {
    map.addLayer({
      id: 'gv-pzsa-poly-fill',
      type: 'fill',
      source: PZSA_POLY_SOURCE,
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.18,
      },
    });
  }
  if (!map.getLayer('gv-pzsa-poly-line')) {
    map.addLayer({
      id: 'gv-pzsa-poly-line',
      type: 'line',
      source: PZSA_POLY_SOURCE,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
        'line-opacity': 0.9,
      },
    });
  }
  if (!map.getLayer('gv-pzsa-poly-label')) {
    map.addLayer({
      id: 'gv-pzsa-poly-label',
      type: 'symbol',
      source: PZSA_POLY_SOURCE,
      minzoom: 11,
      layout: {
        'symbol-placement': 'point',
        'text-field': ['get', 'dispLabel'],
        'text-size': 13,
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': ['match', ['get', 'pzsaType'], 'PZ', LABEL_COLORS.PZ, LABEL_COLORS.SA],
        'text-halo-color': '#0b1119',
        'text-halo-width': 1.2,
      },
    });
  }
  if (!map.getLayer('gv-pzsa-poly-3d')) {
    map.addLayer({
      id: 'gv-pzsa-poly-3d',
      type: 'fill-extrusion',
      source: PZSA_POLY_SOURCE,
      // tubeは平らな天面の角柱にしない（半円の筒はpzsaTubeLayer側で描く）
      filter: ['!=', ['get', 'shape'], 'tube'],
      layout: { visibility: 'none' },
      paint: {
        'fill-extrusion-base': 0,
        'fill-extrusion-height': pzsaCeilingExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': ['get', 'color'],
        'fill-extrusion-opacity': PZSA_3D_OPACITY,
      },
    });
  }
  // tube（中心線＋半径）の3D表示。自前のWebGL描画（tube-layer.ts）。
  // 表示・非表示はmain.tsのupdatePzSaTubes()がメッシュを空にするかで切り替える
  if (!map.getLayer(pzsaTubeLayer.id)) {
    map.addLayer(pzsaTubeLayer);
  }

  // インポートしたスケッチ（TV/TaskviewのXDD等）。塗り+輪郭の2枚構成。
  // 色はPZ/SAのような固定パレットではなくファイル自身が持つ色を使う
  // （data/sketch.ts の sketchToGeoJson が properties.color に入れる）。
  if (!map.getLayer('gv-sketch-fill')) {
    map.addLayer({
      id: 'gv-sketch-fill',
      type: 'fill',
      source: SKETCH_SOURCE,
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.5,
      },
    });
  }
  if (!map.getLayer('gv-sketch-line')) {
    map.addLayer({
      id: 'gv-sketch-line',
      type: 'line',
      source: SKETCH_SOURCE,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 1.5,
        'line-opacity': 0.9,
      },
    });
  }

  // NOTAM高度制限区域（BluePZ）。PZ/SAポリゴンと同じ塗り+輪郭+ラベルの3枚構成
  if (!map.getLayer('gv-notam-fill')) {
    map.addLayer({
      id: 'gv-notam-fill',
      type: 'fill',
      source: NOTAM_SOURCE,
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.18,
      },
    });
  }
  if (!map.getLayer('gv-notam-line')) {
    map.addLayer({
      id: 'gv-notam-line',
      type: 'line',
      source: NOTAM_SOURCE,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
        'line-opacity': 0.9,
      },
    });
  }
  if (!map.getLayer('gv-notam-label')) {
    map.addLayer({
      id: 'gv-notam-label',
      type: 'symbol',
      source: NOTAM_SOURCE,
      minzoom: 10,
      layout: {
        'symbol-placement': 'point',
        'text-field': ['concat', ['get', 'label'], '\n', ['to-string', ['get', 'effectiveAltFt']], 'ft'],
        'text-size': 13,
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#bcd7ff',
        'text-halo-color': '#0b1119',
        'text-halo-width': 1.2,
      },
    });
  }
  // 飛行場中心の参考円（宇都宮飛行場5NM/10NM/15NMなど）。BluePZ本体とは
  // 別ソース・別レイヤーにし、破線＋中立色で「参考情報であり高度制限では
  // ない」ことを見た目でも区別する。塗りは持たない
  if (!map.getLayer('gv-notam-ref-line')) {
    map.addLayer({
      id: 'gv-notam-ref-line',
      type: 'line',
      source: NOTAM_REF_SOURCE,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 1.5,
        'line-opacity': 0.75,
        'line-dasharray': [4, 3],
      },
    });
  }
  if (!map.getLayer('gv-notam-ref-label')) {
    map.addLayer({
      id: 'gv-notam-ref-label',
      type: 'symbol',
      source: NOTAM_REF_SOURCE,
      minzoom: 9,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 300,
        'text-field': ['get', 'label'],
        'text-size': 12,
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': NOTAM_REF_COLOR,
        'text-halo-color': '#0b1119',
        'text-halo-width': 1.2,
      },
    });
  }

  // 3D表示（上空の薄い板）。ゴールピンのような地面からの柱ではなく、
  // ゴール球（ballBaseExpression/ballTopExpression）と同じ「base/topを
  // 両方式にする」パターンで、実効天井の高さに薄い板を浮かせる
  if (!map.getLayer('gv-notam-3d-cap')) {
    map.addLayer({
      id: 'gv-notam-3d-cap',
      type: 'fill-extrusion',
      source: NOTAM_SOURCE,
      layout: { visibility: 'none' },
      paint: {
        'fill-extrusion-base': notamCapBaseExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-height': notamCapTopExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': NOTAM_COLOR,
        'fill-extrusion-opacity': 0.4,
      },
    });
  }

  // ゴール地点
  if (!map.getLayer('gv-goal-circle')) {
    map.addLayer({
      id: 'gv-goal-circle',
      type: 'circle',
      source: GOAL_SOURCE,
      paint: {
        'circle-pitch-alignment': 'map', // 傾けたときに地面に沿わせる（既定はカメラ正面で常に真円）
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 2, 14, 5, 18, 9],
        'circle-color': '#00d9a3',
        'circle-opacity': 0.85,
        'circle-stroke-color': '#04231c',
        'circle-stroke-width': 1,
      },
    });
  }

  // ゴール名称。会場によっては370点あるので、縮小時はビジーになる前に隠す。
  // 密集して重なる場合の間引きは MapLibre の衝突検出（text-allow-overlap
  // の既定値 false）に任せる
  if (!map.getLayer('gv-goal-label')) {
    map.addLayer({
      id: 'gv-goal-label',
      type: 'symbol',
      source: GOAL_SOURCE,
      /*
       * 縮尺ラベルが「2 km」を示すあたりから名称を出す。
       * 実測（390px 幅・渡良瀬）で 3 km → 2 km の切り替わりが z≒11.31
       * だったので、その境目に合わせてある。これより広域だと点が密すぎて
       * 名称が邪魔になる。なお重なった分は MapLibre の衝突判定が自動で
       * 間引くので、拡大側は上限を設けなくてよい。
       *
       * 一覧に入れた点は gv-declared-label が代わりに名前を出す
       * （宣言の輪をよけた位置に出すため、ここでは除く）。
       */
      minzoom: 11.3,
      filter: ['!=', ['get', 'declared'], true],
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 11,
        'text-anchor': 'bottom',
        'text-offset': [0, -1.1],
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#e8eef6',
        'text-halo-color': '#0b1119',
        'text-halo-width': 1.2,
      },
    });
  }

  /*
   * 宣言したゴール。輪だけを描いて中は抜く（下のゴール点を隠さないため）。
   * 半径は「見つけるための目印」なので、拡大率にほぼ依らない画素サイズで
   * 十分。地面の実寸を表しているわけではない点に注意。
   */
  if (!map.getLayer('gv-declared-ring')) {
    map.addLayer({
      id: 'gv-declared-ring',
      type: 'circle',
      source: DECLARED_SOURCE,
      paint: {
        'circle-pitch-alignment': 'map', // 傾けたときに地面に沿わせる（既定はカメラ正面で常に真円）
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 7, 14, 11, 18, 16],
        'circle-opacity': 0,
        'circle-stroke-color': '#4aa8ff',
        'circle-stroke-width': 2,
      },
    });
  }

  // 選択中の1点。370点の中から狙っている点を目で見つけるための強調なので、
  // 宣言済みの青い輪より一回り大きく、赤で描く
  if (!map.getLayer('gv-selected-ring')) {
    map.addLayer({
      id: 'gv-selected-ring',
      type: 'circle',
      source: DECLARED_SOURCE,
      filter: ['==', ['get', 'selected'], true],
      paint: {
        'circle-pitch-alignment': 'map', // 傾けたときに地面に沿わせる（既定はカメラ正面で常に真円）
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 12, 14, 17, 18, 23],
        'circle-opacity': 0,
        'circle-stroke-color': '#ff3b30',
        'circle-stroke-width': 2.5,
      },
    });
  }

  // 高度付きの宣言ゴール（3D表示ON時のみデータが入る柱。地面へ下ろす垂線の役割は側面が果たす）
  if (!map.getSource(GOAL_3D_SOURCE)) {
    map.addSource(GOAL_3D_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getLayer('gv-goal-3d-pin')) {
    map.addLayer({
      id: 'gv-goal-3d-pin',
      type: 'fill-extrusion',
      source: GOAL_3D_SOURCE,
      paint: {
        'fill-extrusion-base': 0,
        'fill-extrusion-height': heightScaleExpression(DEFAULT_HEIGHT_SCALE),
        // 選択中は赤（gv-selected-ringと同じ）、それ以外は青（gv-declared-ringと同じ）
        'fill-extrusion-color': ['case', ['get', 'selected'], '#ff3b30', '#4aa8ff'],
        'fill-extrusion-opacity': 0.9,
      },
    });
  }

  // ゴールの柱の先端に置く球
  if (!map.getSource(GOAL_BALL_3D_SOURCE)) {
    map.addSource(GOAL_BALL_3D_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getLayer('gv-goal-3d-ball')) {
    map.addLayer({
      id: 'gv-goal-3d-ball',
      type: 'fill-extrusion',
      source: GOAL_BALL_3D_SOURCE,
      paint: {
        'fill-extrusion-base': ballBaseExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-height': ballTopExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': ['case', ['get', 'selected'], '#ff3b30', '#4aa8ff'],
        'fill-extrusion-opacity': 0.95,
      },
    });
  }

  /*
   * 宣言したゴールの名称。地図タップで会場の点を選んだ場合と、長押しで
   * 任意座標を登録した場合の両方をこれ1つでまかなう（後者は gv-goal-label
   * の対象外なので、これが無いと名前がまったく出ない）。
   *
   * オフセットを gv-goal-label より深くしてあるのは、選択中の輪
   * （最大 23px）に文字が食われないようにするため。件数は一覧の分だけ
   * （多くて数十）なので、縮小時にビジーになる心配がなく minzoom は設けない。
   */
  if (!map.getLayer('gv-declared-label')) {
    map.addLayer({
      id: 'gv-declared-label',
      type: 'symbol',
      source: DECLARED_SOURCE,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 12,
        'text-anchor': 'bottom',
        'text-offset': [0, -2.0],
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#e8eef6',
        'text-halo-color': '#0b1119',
        'text-halo-width': 1.2,
      },
    });
  }

  // 航跡（高度で色分け）
  if (!map.getLayer('gv-track-line')) {
    map.addLayer({
      id: 'gv-track-line',
      type: 'line',
      source: TRACK_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 2, 14, 4, 18, 7],
        'line-color': altitudeColorExpression(ALTITUDE_STOPS),
      },
    });
  }

  // 3D航跡（高度に応じて空中にプロットした壁。3D表示チェックボックスON時のみデータが入る）
  //
  // ★ tolerance: 0 が必須。既定のtolerance（内部でgeojson-vtが行う簡略化）は、
  //   TRACK_3D_WIDTH_M=4m幅の薄いポリゴンを、ズームアウトして1枚あたりの
  //   footprintが数ピクセル以下になると**縮小ではなく完全に間引いて消す**
  //   （利用者からの指摘「3D表示の航跡が縮小すると高さの線見えなくなっている」
  //   の原因。実測で確認: 24分割の壁がzoom10までは全枚描画されるが、
  //   zoom9以降は1枚も描画されなくなる——単に薄く小さく描かれるのではなく
  //   フィーチャーごと消える。1枚だけの大きなポリゴンでは起きないため、
  //   最初は高さ側の問題だと誤認しかけた）。`tolerance: 0`で簡略化を
  //   止めると、どのズームでも全枚が（実寸なりに小さくはなるが）描かれ
  //   続けることを実機相当のPlaywright検証で確認済み。
  if (!map.getSource(TRACK_3D_SOURCE)) {
    map.addSource(TRACK_3D_SOURCE, { type: 'geojson', data: empty, tolerance: 0 });
  }
  if (!map.getLayer('gv-track-3d-wall')) {
    map.addLayer({
      id: 'gv-track-3d-wall',
      type: 'fill-extrusion',
      source: TRACK_3D_SOURCE,
      paint: {
        'fill-extrusion-base': 0,
        'fill-extrusion-height': heightScaleExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': altitudeColorExpression(ALTITUDE_STOPS),
        // ★ 0.85→0.5。他アプリ（参考画像、パラグライダーのリプレイ）の
        //   「高度を示す壁がシースルーで地形が透けて見える」見た目が良い、
        //   との利用者評（2026-09-14）を受けて下げた。地面から立つ壁は
        //   奥に建つ壁・地形と重なりやすく、不透明だと奥が完全に隠れて
        //   しまうため、半透明にして重なりごしに奥も見えるようにする。
        //   禁止区域（BluePZ/PZ・SA）や自他機の現在位置マーカーは、安全に
        //   関わる／常に一目で見えてほしい表示なので対象外にしている
        //   （利用者の指定どおり、航跡の壁だけを下げた）。
        'fill-extrusion-opacity': 0.5,
      },
    });
  }
  // 壁の天面に沿って浮かせる稜線ライン。以前はfill-extrusionの薄板
  // （gv-track-3d-wall-top）だったが、1枚のポリゴンに一律の高さしか
  // 持たせられず実データの点の間隔なりの「階段」になる制約があった
  // （利用者からの指摘、2026-09-14）。カスタムWebGLレイヤー
  // （RidgeLineLayer）に置き換え、実データの点をそのままgl.LINE_STRIPの
  // 頂点にすることで、GPUのラスタライザによる自動補間で追加コストなしに
  // 滑らかな稜線にする（詳細はridge-line-layer.ts参照。頂点データは
  // main.tsのupdateRidgeLines()がredraw()から都度setStrips()で渡す）。
  if (!map.getLayer(trackRidgeLine.id)) {
    map.addLayer(trackRidgeLine);
  }

  // ライブ受信中の他機（航跡 → 到着予測 → 現在位置 → ラベル の順に重ねる）
  if (!map.getSource(LIVE_TRACK_SOURCE)) {
    map.addSource(LIVE_TRACK_SOURCE, { type: 'geojson', data: empty, tolerance: 0 });
  }
  if (!map.getSource(LIVE_POINT_SOURCE)) {
    map.addSource(LIVE_POINT_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getSource(LIVE_PREDICT_SOURCE)) {
    map.addSource(LIVE_PREDICT_SOURCE, { type: 'geojson', data: empty });
  }
  // 光彩（白い縁取り）。航跡の線の下に、太い白い線を敷く（setLiveTrackGlow）
  if (!map.getLayer('gv-live-line-glow')) {
    map.addLayer({
      id: 'gv-live-line-glow',
      type: 'line',
      source: LIVE_TRACK_SOURCE,
      filter: liveTrackGlowFilter(),
      layout: { 'line-cap': 'round', 'line-join': 'round', visibility: liveTrackGlowMode === 'off' ? 'none' : 'visible' },
      paint: {
        'line-color': '#ffffff',
        // 航跡の線（1.5〜3px）の両側に約3px
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 6, 14, 9],
        'line-blur': 1,
        'line-opacity': 0.9,
      },
    });
  }
  if (!map.getLayer('gv-live-line')) {
    map.addLayer({
      id: 'gv-live-line',
      type: 'line',
      source: LIVE_TRACK_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        // 既定は、保存済みフライトと見比べられるよう高度で色分けする
        // （どの端末かは現在位置のアイコン色 gv-live-point で見分ける）。
        // 機体ごとの単色にも切り替えられる（liveTrackColorMode、docs/08 §218）
        'line-color': liveTrackColorExpression(altitudeColorExpression(ALTITUDE_STOPS)),
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 1.5, 14, 3],
        'line-opacity': 0.75,
      },
    });
  }

  // ライブ端末の3D航跡（gv-track-3d-wallのライブ版。tolerance:0の理由も同じ）
  if (!map.getSource(LIVE_TRACK_3D_SOURCE)) {
    map.addSource(LIVE_TRACK_3D_SOURCE, { type: 'geojson', data: empty, tolerance: 0 });
  }
  if (!map.getLayer('gv-live-track-3d-wall')) {
    map.addLayer({
      id: 'gv-live-track-3d-wall',
      type: 'fill-extrusion',
      source: LIVE_TRACK_3D_SOURCE,
      paint: {
        'fill-extrusion-base': 0,
        'fill-extrusion-height': heightScaleExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': liveTrackColorExpression(altitudeColorExpression(ALTITUDE_STOPS)),
        // ★ gv-track-3d-wall と同じ理由・同じ値で下げてある（上のコメント参照）
        'fill-extrusion-opacity': 0.5,
      },
    });
  }
  // 稜線ラインのライブ版（RidgeLineLayer、同じ理由は上のtrackRidgeLine参照）
  if (!map.getLayer(liveTrackRidgeLine.id)) {
    map.addLayer(liveTrackRidgeLine);
  }

  // 到着予測（30分）。直近の実測点ぶんを扇状に重ねるので、高度による
  // 風向・風速の変化が線の向き・長さの変化として直感的に見える
  if (!map.getLayer('gv-live-predict')) {
    map.addLayer({
      id: 'gv-live-predict',
      type: 'line',
      source: LIVE_PREDICT_SOURCE,
      layout: { 'line-cap': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['case', ['get', 'latest'], 2, 1],
        'line-opacity': ['get', 'opacity'],
      },
    });
  }
  // 他の端末の現在位置も、自機と同じ「白い縁取りの丸＋色付きの三角」にする（利用者の要望、docs/08 §221）。
  // 丸は自機（gv-self-halo）より一回り小さくして、自機が埋もれないようにする。色は端末ごとのまま
  if (!map.getLayer('gv-live-point-halo')) {
    map.addLayer({
      id: 'gv-live-point-halo',
      type: 'circle',
      source: LIVE_POINT_SOURCE,
      paint: {
        'circle-pitch-alignment': 'map',
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 8, 14, 12.5, 18, 17],
        'circle-color': '#ffffff',
        'circle-stroke-color': '#0e1520',
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer('gv-live-point')) {
    map.addLayer({
      id: 'gv-live-point',
      type: 'symbol',
      source: LIVE_POINT_SOURCE,
      layout: {
        'icon-image': ARROW_ICON,
        'icon-rotate': ['coalesce', ['get', 'heading'], 0],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.45, 14, 0.75, 18, 1.05],
      },
      paint: {
        'icon-color': ['get', 'color'],
      },
    });
  }

  // ライブ矢印の高度浮上（3D表示ON時のみデータが入る）。地面に張り付いた
  // gv-live-point（2D）はそのまま残し、こちらは高度分だけ浮いた矢印型の柱を重ねる
  if (!map.getSource(LIVE_POINT_3D_SOURCE)) {
    map.addSource(LIVE_POINT_3D_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getLayer('gv-live-point-3d-arrow')) {
    map.addLayer({
      id: 'gv-live-point-3d-arrow',
      type: 'fill-extrusion',
      source: LIVE_POINT_3D_SOURCE,
      paint: {
        'fill-extrusion-base': 0,
        'fill-extrusion-height': heightScaleExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': ['get', 'color'],
        'fill-extrusion-opacity': 0.9,
      },
    });
  }

  // ライブ矢印の柱の先端に置く球
  if (!map.getSource(LIVE_POINT_BALL_3D_SOURCE)) {
    map.addSource(LIVE_POINT_BALL_3D_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getLayer('gv-live-point-3d-ball')) {
    map.addLayer({
      id: 'gv-live-point-3d-ball',
      type: 'fill-extrusion',
      source: LIVE_POINT_BALL_3D_SOURCE,
      paint: {
        'fill-extrusion-base': ballBaseExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-height': ballTopExpression(DEFAULT_HEIGHT_SCALE),
        'fill-extrusion-color': ['get', 'color'],
        'fill-extrusion-opacity': 0.95,
      },
    });
  }

  // 端末IDのラベル。ゴールラベルと同じく基本は上側、北向きのときだけ
  // 下側に逃がす（labelOffset で計算済み。gv-goal-label 参照）
  if (!map.getLayer('gv-live-label')) {
    map.addLayer({
      id: 'gv-live-label',
      type: 'symbol',
      source: LIVE_POINT_SOURCE,
      layout: {
        'text-field': ['get', 'terminalId'],
        'text-size': 11,
        'text-font': ['Noto Sans Regular'],
        'text-offset': ['get', 'labelOffset'],
        'text-anchor': 'center',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-halo-color': '#0b1119',
        'text-halo-width': 1.4,
      },
    });
  }

  // 他機が宣言したゴール（$_TVMARK）。原典GVの○表示に合わせ、塗らずに輪だけにする
  if (!map.getSource(LIVE_MARK_SOURCE)) {
    map.addSource(LIVE_MARK_SOURCE, { type: 'geojson', data: empty });
  }
  if (!map.getLayer('gv-live-mark')) {
    map.addLayer({
      id: 'gv-live-mark',
      type: 'circle',
      source: LIVE_MARK_SOURCE,
      paint: {
        'circle-pitch-alignment': 'map', // 傾けたときに地面に沿わせる（既定はカメラ正面で常に真円）
        'circle-radius': 7,
        'circle-color': 'transparent',
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer('gv-live-mark-label')) {
    map.addLayer({
      id: 'gv-live-mark-label',
      type: 'symbol',
      source: LIVE_MARK_SOURCE,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 11,
        'text-font': ['Noto Sans Regular'],
        'text-offset': [0, 1.1],
        'text-anchor': 'top',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-halo-color': '#0b1119',
        'text-halo-width': 1.2,
      },
    });
  }

  // タスクシートでタップしたターゲットのカーソル。地図・ゴール・他機より上に出す
  if (!map.getLayer('gv-tbc-mma')) {
    map.addLayer({
      id: 'gv-tbc-mma',
      type: 'line',
      source: TASKBOARD_CURSOR_SOURCE,
      filter: ['==', ['get', 'role'], 'mma'],
      paint: { 'line-color': ['get', 'color'], 'line-width': 2, 'line-dasharray': [2, 1.5] },
    });
  }
  if (!map.getLayer('gv-tbc-halo')) {
    map.addLayer({
      id: 'gv-tbc-halo',
      type: 'circle',
      source: TASKBOARD_CURSOR_SOURCE,
      filter: ['==', ['get', 'role'], 'point'],
      paint: {
        'circle-pitch-alignment': 'map',
        'circle-radius': 16,
        'circle-color': 'transparent',
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 5,
      },
    });
  }
  if (!map.getLayer('gv-tbc-ring')) {
    map.addLayer({
      id: 'gv-tbc-ring',
      type: 'circle',
      source: TASKBOARD_CURSOR_SOURCE,
      filter: ['==', ['get', 'role'], 'point'],
      paint: {
        'circle-pitch-alignment': 'map',
        'circle-radius': 16,
        'circle-color': 'transparent',
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 2.5,
      },
    });
  }
  // 輪の中の十字（画像。中心が輪の中心にきっちり重なる、createCrossIconの注記参照）
  for (const [id, icon, color] of [
    ['gv-tbc-cross-halo', CROSS_HALO_ICON, '#ffffff'],
    ['gv-tbc-cross', CROSS_ICON, ['get', 'color']],
  ] as const) {
    if (map.getLayer(id)) continue;
    map.addLayer({
      id,
      type: 'symbol',
      source: TASKBOARD_CURSOR_SOURCE,
      filter: ['==', ['get', 'role'], 'point'],
      layout: {
        'icon-image': icon,
        'icon-anchor': 'center',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: { 'icon-color': color as ExpressionSpecification | string },
    });
  }
  if (!map.getLayer('gv-tbc-label')) {
    map.addLayer({
      id: 'gv-tbc-label',
      type: 'symbol',
      source: TASKBOARD_CURSOR_SOURCE,
      filter: ['==', ['get', 'role'], 'point'],
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 13,
        'text-font': ['Noto Sans Regular'],
        'text-offset': [0, 1.9],
        'text-anchor': 'top',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#ffffff', 'text-halo-color': '#0b1119', 'text-halo-width': 2 },
    });
  }

  // 自機位置。他機より上に重ねて、常に自分の矢印が隠れないようにする。
  // 白い縁取りの丸を下敷きにして、地図の色に関わらず見つけやすくする
  if (!map.getLayer('gv-self-halo')) {
    map.addLayer({
      id: 'gv-self-halo',
      type: 'circle',
      source: SELF_SOURCE,
      paint: {
        'circle-pitch-alignment': 'map', // 傾けたときに地面に沿わせる（既定はカメラ正面で常に真円）
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 9, 14, 14, 18, 19],
        'circle-color': '#ffffff',
        'circle-stroke-color': '#0e1520',
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer('gv-self-point')) {
    map.addLayer({
      id: 'gv-self-point',
      type: 'symbol',
      source: SELF_SOURCE,
      layout: {
        'icon-image': ARROW_ICON,
        'icon-rotate': ['coalesce', ['get', 'heading'], 0],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.5, 14, 0.85, 18, 1.15],
      },
      paint: {
        'icon-color': SELF_COLOR,
      },
    });
  }

  // 自機位置の手動入力の仮の点。自機と同じ白縁取り＋色丸の構成（向きは持たない）
  if (!map.getLayer('gv-manual-position-halo')) {
    map.addLayer({
      id: 'gv-manual-position-halo',
      type: 'circle',
      source: MANUAL_POSITION_SOURCE,
      paint: {
        'circle-pitch-alignment': 'map',
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 9, 14, 14, 18, 19],
        'circle-color': '#ffffff',
        'circle-stroke-color': '#0e1520',
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer('gv-manual-position-point')) {
    map.addLayer({
      id: 'gv-manual-position-point',
      type: 'circle',
      source: MANUAL_POSITION_SOURCE,
      paint: {
        'circle-pitch-alignment': 'map',
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 6, 14, 9, 18, 13],
        'circle-color': MANUAL_POSITION_COLOR,
      },
    });
  }

  // 離着陸点
  if (!map.getLayer('gv-endpoint')) {
    map.addLayer({
      id: 'gv-endpoint',
      type: 'circle',
      source: POINT_SOURCE,
      paint: {
        'circle-pitch-alignment': 'map', // 傾けたときに地面に沿わせる（既定はカメラ正面で常に真円）
        'circle-radius': 7,
        'circle-color': ['match', ['get', 'kind'], 'launch', '#ffffff', '#ff4d6d'],
        'circle-stroke-color': '#0e1520',
        'circle-stroke-width': 2,
      },
    });
  }

  /*
   * 選択中ゴールへの点線。現在地→ゴールの直線＋方位/距離のラベル。
   * gv-lrn3dt-plan-line と同じ「破線はレイヤーを分けるしかない」流儀の
   * 点線＋gv-utm-grid-labelと同じ「線に沿って文字を置く」symbolレイヤーの組み合わせ。
   *
   * ★ ここまでの他の地物（PZ/SA・NOTAM・競技エリア・ゴール・航跡等）より
   *   あとに追加すること。MapLibreのaddLayer()はbeforeIdを渡さない限り
   *   常に最前面に積むため、先に追加してしまうとSA等の塗りに隠れて見えなく
   *   なる不具合を実機で踏んだ（自機（gv-self-*）と同じく「常に見えている
   *   べき地物」として、当たり判定専用レイヤー（opacity:0）の直前＝
   *   見た目のある地物としては最前面に置く）。
   */
  if (!map.getLayer('gv-goal-bearing-line')) {
    map.addLayer({
      id: 'gv-goal-bearing-line',
      type: 'line',
      source: GOAL_BEARING_SOURCE,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': GOAL_BEARING_COLOR,
        'line-width': 2.5,
        'line-opacity': 0.9,
        'line-dasharray': [1.5, 1.5],
      },
    });
  }
  if (!map.getLayer('gv-goal-bearing-label')) {
    map.addLayer({
      id: 'gv-goal-bearing-label',
      type: 'symbol',
      source: GOAL_BEARING_SOURCE,
      layout: {
        'symbol-placement': 'line-center',
        'text-field': ['get', 'label'],
        'text-size': 13,
        'text-font': ['Noto Sans Regular'],
        'text-keep-upright': true,
      },
      paint: {
        'text-color': GOAL_BEARING_COLOR,
        'text-halo-color': '#0b1119',
        'text-halo-width': 1.4,
      },
    });
  }

  /*
   * 計測モードで描いた図形（直線・円・多角形）。
   * ★ ゴールへの点線と同じく、他の地物より**あと**に積む
   *   （SA等の塗りに隠れる不具合を踏んだ経緯は上のコメント参照）。
   * `role` プロパティでレイヤーを振り分ける（data/measure.ts が付ける）。
   */
  if (!map.getLayer('gv-measure-fill')) {
    map.addLayer({
      id: 'gv-measure-fill',
      type: 'fill',
      source: MEASURE_SOURCE,
      filter: ['==', ['get', 'role'], 'fill'],
      // 下の地図・区域が読めなくならない濃さにする（PZ/SAの塗りと同じ考え方）
      paint: { 'fill-color': MEASURE_COLOR, 'fill-opacity': 0.12 },
    });
  }
  if (!map.getLayer('gv-measure-line')) {
    map.addLayer({
      id: 'gv-measure-line',
      type: 'line',
      source: MEASURE_SOURCE,
      // 区間（seg）と輪郭（ring）の両方を実線で描く
      filter: ['match', ['get', 'role'], ['seg', 'ring'], true, false],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': MEASURE_COLOR, 'line-width': 2.5, 'line-opacity': 0.95 },
    });
  }
  if (!map.getLayer('gv-measure-vertex')) {
    map.addLayer({
      id: 'gv-measure-vertex',
      type: 'circle',
      source: MEASURE_SOURCE,
      // 直線の終点（矢印）は gv-measure-vertex-arrow が描くので、ここでは除く
      filter: ['all', ['==', ['get', 'role'], 'vertex'], ['!=', ['get', 'vertexRole'], 'end']],
      paint: {
        'circle-pitch-alignment': 'map',
        // 直線の始点は「○」に見せたい：塗りを消して縁取りだけにする
        'circle-radius': ['case', ['==', ['get', 'vertexRole'], 'start'], 8, 6],
        'circle-color': MEASURE_COLOR,
        'circle-opacity': ['case', ['==', ['get', 'vertexRole'], 'start'], 0, 1],
        // 白い縁取りで「掴める点」だと分かるようにする（自機・仮の点と同じ流儀）
        'circle-stroke-color': ['case', ['==', ['get', 'vertexRole'], 'start'], MEASURE_COLOR, '#ffffff'],
        'circle-stroke-width': ['case', ['==', ['get', 'vertexRole'], 'start'], 2.5, 2],
      },
    });
  }
  if (!map.getLayer('gv-measure-vertex-arrow')) {
    map.addLayer({
      id: 'gv-measure-vertex-arrow',
      type: 'symbol',
      source: MEASURE_SOURCE,
      filter: ['all', ['==', ['get', 'role'], 'vertex'], ['==', ['get', 'vertexRole'], 'end']],
      layout: {
        // ★ 新しいアイコンは作らず、ライブ端末の進行方向マーカーと同じ北向き
        //   三角形のSDFアイコンを流用する（0°=北・時計回りの基準もheadingと共通）
        'icon-image': ARROW_ICON,
        'icon-size': 0.4,
        'icon-rotate': ['coalesce', ['get', 'bearing'], 0],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      paint: { 'icon-color': MEASURE_COLOR },
    });
  }
  if (!map.getLayer('gv-measure-seg-label')) {
    map.addLayer({
      id: 'gv-measure-seg-label',
      type: 'symbol',
      source: MEASURE_SOURCE,
      filter: ['==', ['get', 'role'], 'seg'],
      layout: {
        // ★ symbol-placement は式にできないので、線の中央に置くラベルと
        //   点に置くラベルはレイヤーを分けるしかない
        'symbol-placement': 'line-center',
        'text-field': ['get', 'label'],
        'text-size': 13,
        'text-font': ['Noto Sans Regular'],
        'text-keep-upright': true,
      },
      paint: { 'text-color': MEASURE_COLOR, 'text-halo-color': '#0b1119', 'text-halo-width': 1.4 },
    });
  }
  if (!map.getLayer('gv-measure-label')) {
    map.addLayer({
      id: 'gv-measure-label',
      type: 'symbol',
      source: MEASURE_SOURCE,
      filter: ['==', ['get', 'role'], 'label'],
      layout: {
        'symbol-placement': 'point',
        'text-field': ['get', 'label'],
        'text-size': 13,
        'text-font': ['Noto Sans Regular'],
        'text-allow-overlap': true,
      },
      paint: { 'text-color': MEASURE_COLOR, 'text-halo-color': '#0b1119', 'text-halo-width': 1.4 },
    });
  }

  // プランナー（LRN/3DT）の入力点。自機位置の仮の点と同じ白縁取り＋色丸の構成
  if (!map.getLayer('gv-lrn3dt-point-halo')) {
    map.addLayer({
      id: 'gv-lrn3dt-point-halo',
      type: 'circle',
      source: LRN3DT_POINT_SOURCE,
      paint: {
        'circle-pitch-alignment': 'map',
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 9, 14, 14, 18, 19],
        'circle-color': '#ffffff',
        'circle-stroke-color': '#0e1520',
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer('gv-lrn3dt-point')) {
    map.addLayer({
      id: 'gv-lrn3dt-point',
      type: 'circle',
      source: LRN3DT_POINT_SOURCE,
      paint: {
        'circle-pitch-alignment': 'map',
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 6, 14, 9, 18, 13],
        'circle-color': LRN3DT_PLAN_COLOR,
      },
    });
  }
  if (!map.getLayer('gv-lrn3dt-point-label')) {
    map.addLayer({
      id: 'gv-lrn3dt-point-label',
      type: 'symbol',
      source: LRN3DT_POINT_SOURCE,
      layout: {
        'symbol-placement': 'point',
        'text-field': ['get', 'label'],
        'text-size': 12,
        'text-font': ['Noto Sans Regular'],
        'text-offset': [0, 1.4],
        'text-allow-overlap': true,
      },
      paint: { 'text-color': LRN3DT_PLAN_COLOR, 'text-halo-color': '#0b1119', 'text-halo-width': 1.4 },
    });
  }

  /*
   * タップ用の当たり判定。
   *
   * 見た目の点は半径 2〜9px しかなく、指ではまず押せない（実機で
   * 「なかなか選択できない」と確認済み）。当たり判定だけを大きく取った
   * 透明な層を最前面に重ね、イベントはこちらで拾う。
   *
   * ★ 判定を広げると1回のタップに複数の点が入る。どれを採るかは
   *   描画順まかせにせず、呼び出し側で「タップ位置に最も近い点」を
   *   選び直すこと（layers.ts だけでは狙いと食い違う）。
   *
   * opacity:0 でも当たり判定は残る（MapLibre は描画の透明度では
   *   絞り込まない）ので、見た目に影響を与えずに広げられる。
   */
  if (!map.getLayer('gv-track-hit')) {
    map.addLayer({
      id: 'gv-track-hit',
      type: 'line',
      source: TRACK_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-width': 24, 'line-opacity': 0 },
    });
  }
  // ライブ航跡（履歴線）用の当たり判定。現在位置だけでなく途中の実測点も読めるように
  if (!map.getLayer('gv-live-track-hit')) {
    map.addLayer({
      id: 'gv-live-track-hit',
      type: 'line',
      source: LIVE_TRACK_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-width': 24, 'line-opacity': 0 },
    });
  }
  if (!map.getLayer('gv-goal-hit')) {
    map.addLayer({
      id: 'gv-goal-hit',
      type: 'circle',
      source: GOAL_SOURCE,
      paint: {
        // 見た目（gv-goal-circle）と当たり判定の位置がずれないよう揃える
        'circle-pitch-alignment': 'map',
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 10, 13, 16, 18, 22],
        'circle-opacity': 0,
      },
    });
  }
  if (!map.getLayer('gv-live-hit')) {
    map.addLayer({
      id: 'gv-live-hit',
      type: 'circle',
      source: LIVE_POINT_SOURCE,
      paint: { 'circle-pitch-alignment': 'map', 'circle-radius': 22, 'circle-opacity': 0 },
    });
  }
}

/**
 * 直前に setData() した参照を覚えておき、同じ参照なら呼び直さない。
 *
 * ★ redraw() は1秒ごとに走る（main.tsのコメント参照、ライブ更新と無関係に
 *   常時）。HAZARD_SOURCE・UTM_GRID_SOURCEのように「会場切替時に1回だけ
 *   組んで state に持ち回す」静的な大きいGeoJSON（佐賀のhazards.geojsonは
 *   42,534フィーチャー・生11.5MB）は、変化が無いのに毎秒setData()され、
 *   MapLibre側の再インデックスが積み重なって描画が不安定になる（実機で、
 *   同じフィーチャー集合でも変わらず再構築コストが発生することを確認済み）。
 *   呼び出し側が同じオブジェクト参照を渡す限り、ここで弾ける。
 */
const lastSourceData = new WeakMap<GeoJSONSource, GeoJSON.FeatureCollection>();

export function setSourceData(
  map: MapLibreMap,
  id: string,
  data: GeoJSON.FeatureCollection,
): void {
  const source = map.getSource(id) as GeoJSONSource | undefined;
  if (!source) return;
  if (lastSourceData.get(source) === data) return;
  lastSourceData.set(source, data);
  source.setData(data);
}

/** 航跡が収まる範囲 */
export function boundsOf(fixes: FixSample[]): [[number, number], [number, number]] | null {
  if (fixes.length === 0) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const f of fixes) {
    west = Math.min(west, f.lon);
    east = Math.max(east, f.lon);
    south = Math.min(south, f.lat);
    north = Math.max(north, f.lat);
  }
  return [
    [west, south],
    [east, north],
  ];
}
